<template>
  <div :class="['workspace-v2', 'workspace-v2--event', 'seller-swap-page', { 'seller-swap-page--embedded': embedded }]">
    <OrganizerWorkspaceTopbar v-if="!embedded" :menu-open="showNav && isMobileViewport" menu-label="Abrir menu de FOMO Swap" :brand-route="swapSellerHomeRoute" @open-menu="showNav = true" />

    <OrganizerWorkspaceNav
      v-if="!embedded"
      class="workspace-v2__nav--mobile"
      :mobile-open="showNav && isMobileViewport"
      :drawer-mode="true"
      :brand-route="swapSellerHomeRoute"
      workspace-eyebrow="FOMO"
      context-eyebrow="FOMO Swap"
      :context-label="sellerNavContextLabel"
      :workspace-items="sellerWorkspaceNavItems"
      :context-items="sellerContextNavItems"
      @close="showNav = false"
    />

    <div class="workspace-v2__shell workspace-v2__shell--event seller-swap-shell">
      <OrganizerWorkspaceNav
        v-if="!embedded"
        class="workspace-v2__nav--desktop"
        :mobile-open="showNav"
        :brand-route="swapSellerHomeRoute"
        workspace-eyebrow="FOMO"
        context-eyebrow="FOMO Swap"
        :context-label="sellerNavContextLabel"
        :workspace-items="sellerWorkspaceNavItems"
        :context-items="sellerContextNavItems"
        @close="showNav = false"
      />

      <main class="workspace-v2__main seller-swap-main">
      <section class="seller-swap-hero glass-surface">
        <div class="seller-swap-hero__bar">
          <div class="seller-swap-section-chip">
              <small>FOMO Swap</small>
            <strong>{{ activeSectionConfig.label }}</strong>
          </div>

          <div class="seller-swap-hero__actions">
            <button class="seller-swap-hero-action" type="button" :disabled="heroActionDisabled" @click="handleHeroAction">
              <i :class="heroActionIcon"></i>
              <span>{{ heroActionLabel }}</span>
            </button>
          </div>
        </div>

        <div class="seller-swap-hero__main">
          <div class="seller-swap-hero__copy">
            <h1>{{ activeSectionConfig.title }}</h1>
            <p>{{ activeSectionConfig.description }}</p>
          </div>
        </div>
      </section>

      <nav v-if="embedded && showEmbeddedMenu" class="seller-swap-embedded-menu" aria-label="Menu de FOMO Swap">
        <button v-for="item in navItems" :key="item.key" type="button" :class="['seller-swap-embedded-menu__item', { 'is-active': item.key === activeSection }]" @click="setEmbeddedSection(item.key)">
          <i :class="item.icon"></i>
          <span>{{ item.label }}</span>
          <small>{{ item.helper }}</small>
        </button>
      </nav>

      <div v-if="feedback" :class="['seller-swap-feedback', feedbackType === 'error' ? 'is-error' : 'is-success']">{{ feedback }}</div>

      <section v-if="activeSection === 'sell'" class="seller-swap-route-section">
        <article v-if="profileNeedsCompletion" class="seller-swap-profile-gate glass-surface">
          <div>
            <span class="workspace-v2__kicker">Perfil requerido</span>
            <h2>Activa tu perfil antes de publicar</h2>
            <p>Completa tus datos para publicar entradas.</p>
          </div>
          <button v-if="embedded" class="seller-swap-button seller-swap-button--primary seller-swap-link-button" type="button" @click="setEmbeddedSection('profile')">Completar perfil</button>
          <router-link v-else class="seller-swap-button seller-swap-button--primary seller-swap-link-button" :to="{ name: 'swap-seller-profile' }">Completar perfil</router-link>
        </article>

        <article class="seller-swap-sell-experience glass-surface">
          <div class="seller-swap-sell-head">
            <div>
              <span class="workspace-v2__kicker">Nueva venta</span>
              <h2>Publica una entrada real</h2>
              <p>Selecciona el evento, define el precio visible y deja cargada la evidencia correcta para que FOMO pueda proteger la venta.</p>
            </div>
            <div class="seller-swap-ready-meter">
              <small>Tu entrada está</small>
              <strong>{{ sellProgress }}%</strong>
              <span>lista para venderse</span>
            </div>
          </div>

          <div class="seller-swap-stepper" role="tablist" aria-label="Pasos para vender">
            <button v-for="(step, index) in sellSteps" :key="step.key" type="button" :class="['seller-swap-stepper__item', { 'is-active': saleStep === step.key, 'is-done': sellStepIsDone(step.key) }]" @click="goSaleStep(step.key)">
              <span>{{ index + 1 }}</span>
              <strong>{{ step.label }}</strong>
            </button>
          </div>

          <div class="seller-swap-sale-lab">
            <div class="seller-swap-sale-stage">
              <section v-if="saleStep === 'event'" class="seller-swap-step-card">
                <span class="workspace-v2__kicker">Paso 1</span>
                <h3>¿Para qué evento tienes entrada?</h3>
                <p>Selecciona el evento Swap activo donde quieres aparecer.</p>
                <div class="seller-swap-event-cards">
                  <button v-for="event in events" :key="event.id" type="button" :class="['seller-swap-event-card', { 'is-active': Number(listingForm.event) === Number(event.id) }]" @click="selectListingEvent(event)">
                    <small>FOMO Swap</small>
                    <strong>{{ event.name }}</strong>
                    <span>{{ formatDate(event.start_date) }}</span>
                  </button>
                </div>
              </section>

              <section v-else-if="saleStep === 'category'" class="seller-swap-step-card">
                <span class="workspace-v2__kicker">Paso 2</span>
                <h3>Elige el tipo exacto de entrada</h3>
                <p>Usamos la categoría oficial para que el comprador entienda qué compra y para calcular un precio sugerido serio.</p>
                <div v-if="pricingOptionsLoading" class="seller-swap-empty">Calculando precios sugeridos...</div>
                <div v-else-if="!pricingOptions.length" class="seller-swap-empty">Este evento aún no tiene categorías oficiales configuradas para reventa.</div>
                <div v-else class="seller-swap-category-grid">
                  <button v-for="option in pricingOptions" :key="option.id" type="button" :class="['seller-swap-category-card', { 'is-active': Number(listingForm.official_ticket) === Number(option.id) }]" @click="selectPricingOption(option)">
                    <span class="seller-swap-category-card__icon"><i :class="categoryIcon(option)"></i></span>
                    <span class="seller-swap-category-card__body">
                      <small>{{ categoryKindLabel(option) }}</small>
                      <strong>{{ option.name }}</strong>
                      <span>{{ categoryCardCopy(option) }}</span>
                    </span>
                    <span class="seller-swap-category-card__price">
                      <small>Oficial</small>
                      <strong>{{ money(option.official_price, option.currency) }}</strong>
                      <em>{{ option.insight_title }}</em>
                    </span>
                  </button>
                </div>
              </section>

              <section v-else-if="saleStep === 'quantity'" class="seller-swap-step-card">
                <span class="workspace-v2__kicker">Paso 3</span>
                <h3>{{ selectedPricingOption && selectedPricingOption.is_package ? 'Paquete completo' : '¿Cuántas entradas vendes?' }}</h3>
                <p>{{ selectedPricingOption && selectedPricingOption.is_package ? 'Esta categoría se vende como una sola unidad completa.' : 'Elige una cantidad rápida. Puedes agregar asientos o referencias luego.' }}</p>
                <div class="seller-swap-quantity-grid">
                  <button v-for="quantity in quantityChoices" :key="quantity" type="button" :class="['seller-swap-quantity-card', { 'is-active': Number(listingForm.quantity) === Number(quantity) }]" @click="setListingQuantity(quantity)">
                    <strong>{{ selectedPricingOption && selectedPricingOption.is_package ? 'Paquete' : quantity }}</strong>
                    <span>{{ quantityLabel(quantity) }}</span>
                  </button>
                </div>
                <button class="seller-swap-button seller-swap-button--primary" type="button" :disabled="!selectedPricingOption" @click="goSaleStep('price')">Continuar al precio</button>
              </section>

              <section v-else-if="saleStep === 'price'" class="seller-swap-step-card seller-swap-price-step">
                <span class="workspace-v2__kicker">Paso 4</span>
                <h3>Define el precio por entrada</h3>
                <p>Este es el precio que verá el comprador. FOMO te muestra la referencia oficial y de mercado para que publiques con confianza.</p>
                <div class="seller-swap-price-hero">
                  <div>
                    <small>Precio comprador</small>
                    <strong>{{ money(listingForm.unit_price || suggestedPrice, 'USD') }}</strong>
                    <span>{{ quantityLabel(listingForm.quantity) }} · total potencial {{ money(estimatedSellerTotal, 'USD') }}</span>
                  </div>
                  <div class="seller-swap-price-hero__source">
                    <i class="fas fa-chart-line"></i>
                    <strong>{{ selectedPricingOption ? selectedPricingOption.recommended_price_source_label : 'Precio sugerido' }}</strong>
                    <span>Tu precio puede ajustarse, pero no debe parecer improvisado.</span>
                  </div>
                </div>
                <div class="seller-swap-price-bars">
                  <div><span>Oficial</span><strong>{{ money(officialPrice, 'USD') }}</strong></div>
                  <div><span>{{ selectedPricingOption && selectedPricingOption.comparable_seller_count >= 10 ? 'Mercado' : 'Referencia' }}</span><strong>{{ money(marketReferencePrice, 'USD') }}</strong></div>
                  <div><span>Tu precio</span><strong>{{ money(listingForm.unit_price, 'USD') }}</strong></div>
                </div>
                <div class="seller-swap-price-actions">
                  <button class="seller-swap-button seller-swap-button--primary" type="button" :disabled="!selectedPricingOption" @click="useSuggestedPrice">Usar sugerido</button>
                  <label class="seller-swap-price-input">
                    <span>Tu precio por entrada (USD)</span>
                    <input v-model="listingForm.unit_price" min="0.01" step="0.01" type="number" />
                  </label>
                </div>
                <div class="seller-swap-price-guidance">
                  <article>
                    <i class="fas fa-eye"></i>
                    <strong>Visible y claro</strong>
                    <span>El comprador verá este valor como precio por entrada.</span>
                  </article>
                  <article>
                    <i class="fas fa-shield-alt"></i>
                    <strong>Pago protegido</strong>
                    <span>FOMO retiene y valida antes de liberar dinero.</span>
                  </article>
                </div>
                <div v-if="pricePolicyWarning" class="seller-swap-price-warning">{{ pricePolicyWarning }}</div>
                <button class="seller-swap-button seller-swap-button--primary" type="button" :disabled="priceBelowOfficialBlocked" @click="goSaleStep('delivery')">Listo, seguir a entrega</button>
              </section>

              <section v-else-if="saleStep === 'delivery'" class="seller-swap-step-card">
                <span class="workspace-v2__kicker">Paso 5</span>
                <h3>Tipo de entrada y evidencia</h3>
                <p>Indica si es QR final, QR para canje, transferencia por app o físico. Si es QR, sube el archivo o deja una referencia verificable antes de publicar.</p>
                <div class="seller-swap-delivery-choice-grid">
                  <button v-for="method in deliveryMethods" :key="method.value" type="button" :class="['seller-swap-delivery-choice', { 'is-active': listingForm.delivery_method === method.value }]" @click="listingForm.delivery_method = method.value">
                    <i :class="deliveryMethodIcon(method.value)"></i>
                    <span>
                      <strong>{{ method.label }}</strong>
                      <small>{{ deliveryChoiceBadge(method.value) }}</small>
                      <em>{{ deliveryChoiceCopy(method.value) }}</em>
                    </span>
                  </button>
                </div>
                <div class="seller-swap-form seller-swap-form--two seller-swap-sale-details">
                  <label>
                    <span>Fila o referencia de ubicación</span>
                    <input v-model.trim="listingForm.row_label" type="text" placeholder="A, B, mesa 12..." />
                  </label>
                  <label class="seller-swap-check seller-swap-check--inline">
                    <input v-model="listingForm.seats_together" type="checkbox" />
                    <span>Asientos juntos</span>
                  </label>
                  <label v-if="isQrDeliveryMethod(listingForm.delivery_method)" class="seller-swap-field--wide">
                    <span>Cómo viene el QR</span>
                    <select v-model="listingForm.fulfillment_mode">
                      <option value="per_unit">Un QR o referencia por entrada</option>
                      <option value="package">Un QR o referencia para todo el paquete</option>
                    </select>
                  </label>
                  <label class="seller-swap-field--wide">
                    <span>Asientos opcional</span>
                    <textarea v-model="listingForm.seat_labels_text" rows="3" placeholder="A1, A2 o una línea por entrada"></textarea>
                  </label>
                  <label v-if="!deliveryEvidenceRequired" class="seller-swap-field--wide">
                    <span>Referencia opcional</span>
                    <textarea v-model="listingForm.external_refs_text" rows="3" placeholder="Código, folio o referencia si ya la tienes"></textarea>
                  </label>
                </div>
                <div v-if="deliveryEvidenceRequired" :class="['seller-swap-delivery-proof-card', { 'is-ready': deliveryEvidenceReady }]">
                  <div class="seller-swap-delivery-proof-card__head">
                    <span><i :class="deliveryMethodIcon(listingForm.delivery_method)"></i>{{ deliveryPrimaryProofLabel }}</span>
                    <strong>{{ deliveryEvidenceReady ? 'Evidencia lista' : 'Falta QR o referencia' }}</strong>
                    <p>{{ deliveryProofHelper }}</p>
                  </div>
                  <label class="seller-swap-file-drop">
                    <input type="file" accept="image/*,.pdf" @change="setListingDraftProofFile" />
                    <span class="seller-swap-file-drop__icon"><i class="fas fa-cloud-upload-alt"></i></span>
                    <span>
                      <strong>{{ listingProofDraftFileName }}</strong>
                      <small>PNG, JPG o PDF. El comprador no lo recibe hasta que FOMO confirme el pago.</small>
                    </span>
                  </label>
                  <div class="seller-swap-form seller-swap-form--two">
                    <label class="seller-swap-field--wide">
                      <span>{{ listingForm.fulfillment_mode === 'package' ? 'Referencia del paquete' : 'Referencia por entrada' }}</span>
                      <textarea v-model="listingForm.external_refs_text" rows="3" :placeholder="sellerReferencePlaceholder"></textarea>
                      <small>{{ referenceCountHint }}</small>
                    </label>
                    <label class="seller-swap-field--wide">
                      <span>Nota para revisión FOMO</span>
                      <input v-model.trim="listingForm.proof_note" type="text" placeholder="Ej: QR final comprado en Ticketmaster, listo para transferir" />
                    </label>
                  </div>
                </div>
                <button class="seller-swap-button seller-swap-button--primary" type="button" :disabled="!deliveryEvidenceReady" @click="goSaleStep('publish')">{{ deliveryEvidenceReady ? 'Ver resumen' : 'Carga QR o referencia' }}</button>
              </section>

              <section v-else class="seller-swap-step-card seller-swap-publish-step">
                <span class="workspace-v2__kicker">Paso 6</span>
                <h3>Publicación lista</h3>
                <p>Revisa precio, tipo de entrada y evidencia. Si algo falta, FOMO bloquea la publicación antes de que parezca incompleta.</p>
                <div class="seller-swap-final-summary">
                  <article>
                    <i class="fas fa-ticket-alt"></i>
                    <span>Entrada</span>
                    <strong>{{ publishPreviewTitle }}</strong>
                    <em>{{ quantityLabel(listingForm.quantity) }}</em>
                  </article>
                  <article>
                    <i class="fas fa-tag"></i>
                    <span>Precio comprador</span>
                    <strong>{{ money(listingForm.unit_price, 'USD') }} c/u</strong>
                    <em>Total potencial {{ money(estimatedSellerTotal, 'USD') }}</em>
                  </article>
                  <article>
                    <i :class="deliveryMethodIcon(listingForm.delivery_method)"></i>
                    <span>Entrega</span>
                    <strong>{{ deliveryMethodLabel(listingForm.delivery_method) }}</strong>
                    <em>{{ deliveryEvidenceRequired ? (deliveryEvidenceReady ? 'QR/evidencia cargada' : 'Falta QR/evidencia') : 'Evidencia no requerida al publicar' }}</em>
                  </article>
                </div>
                <button class="seller-swap-button seller-swap-button--primary seller-swap-publish-button" type="button" :disabled="savingListing || !canSubmitListing" @click="createListing">{{ savingListing ? 'Publicando...' : 'Publicar y empezar a vender' }}</button>
              </section>
            </div>

            <aside class="seller-swap-live-preview">
              <div class="seller-swap-live-preview__visual">
                <small>Preview comprador</small>
                <span class="seller-swap-live-preview__icon"><i :class="deliveryMethodIcon(listingForm.delivery_method)"></i></span>
                <strong>{{ publishPreviewTitle }}</strong>
                <span>{{ selectedEvent ? selectedEvent.name : 'Elige un evento' }}</span>
              </div>
              <div class="seller-swap-live-preview__price">
                <small>Precio visible</small>
                <strong>{{ listingForm.unit_price ? money(listingForm.unit_price, 'USD') : money(suggestedPrice, 'USD') }}</strong>
                <span>{{ selectedPricingOption ? selectedPricingOption.insight_copy : 'Elige una categoría para calcular oportunidad.' }}</span>
              </div>
              <div :class="['seller-swap-live-preview__proof', livePreviewProofClass]">
                <i :class="livePreviewProofIcon"></i>
                <span>{{ livePreviewProofLabel }}</span>
              </div>
              <div class="seller-swap-live-preview__badges">
                <span>Pago protegido</span>
                <span>{{ selectedPricingOption && selectedPricingOption.comparable_seller_count >= 10 ? 'Precio de mercado' : 'Alta oportunidad' }}</span>
                <span>{{ isQrDeliveryMethod(listingForm.delivery_method) ? 'QR primero' : 'Entrega guiada' }}</span>
              </div>
            </aside>
          </div>
        </article>
      </section>

      <section v-if="activeSection === 'tickets'" class="seller-swap-panel glass-surface">
        <div class="seller-swap-panel__header seller-swap-panel__header--stacked">
          <div>
            <span class="workspace-v2__kicker">Entradas</span>
            <h2>Entradas y ventas</h2>
            <p>Gestiona publicación, entrega y mensajes desde una sola lista.</p>
          </div>
          <button class="seller-swap-button" type="button" :disabled="loading" @click="loadAll">Actualizar</button>
        </div>

        <div class="seller-swap-entry-filters" role="tablist" aria-label="Filtrar entradas">
          <button v-for="filter in entryFilters" :key="filter.key" type="button" :class="['seller-swap-entry-filter', { 'is-active': entryFilter === filter.key }]" @click="entryFilter = filter.key">
            <span>{{ filter.label }}</span>
            <strong>{{ filter.count }}</strong>
          </button>
        </div>

        <div v-if="loading && !entryRows.length" class="seller-swap-empty">Cargando entradas...</div>
        <div v-else-if="!entryRows.length" class="seller-swap-empty">Aún no tienes entradas publicadas ni ventas por entregar.</div>
        <div v-else-if="!filteredEntryRows.length" class="seller-swap-empty">No hay entradas en este filtro.</div>
        <div v-else class="seller-swap-entry-table">
          <article v-for="row in filteredEntryRows" :key="row.key" class="seller-swap-entry-row">
            <div class="seller-swap-entry-row__main">
              <div class="seller-swap-entry-row__entry">
                <small>{{ row.kind === 'delivery' ? row.orderNumber : row.eventDate }}</small>
                <strong>{{ row.title }}</strong>
                <span>{{ row.subtitle }}</span>
                <em>{{ row.seat }}</em>
              </div>
              <div class="seller-swap-entry-row__status">
                <span :class="['seller-swap-pill', row.primaryStatusClass]">{{ row.primaryStatusLabel }}</span>
                <span :class="['seller-swap-pill', row.secondaryStatusClass]">{{ row.secondaryStatusLabel }}</span>
              </div>
              <div class="seller-swap-entry-row__money">
                <small>{{ row.moneyKicker }}</small>
                <strong>{{ row.amountLabel }}</strong>
                <span>{{ row.moneyHelper }}</span>
              </div>
              <div class="seller-swap-entry-row__actions">
                <button :class="['seller-swap-button', { 'seller-swap-button--primary': row.kind === 'delivery' && canSubmitDeliveryHandoff(row.delivery) }]" type="button" @click="toggleEntryDetails(row.key)">{{ expandedEntryKey === row.key ? 'Ocultar' : row.primaryActionLabel }}</button>
                <button v-if="row.kind === 'listing' && row.listing.safe_actions && row.listing.safe_actions.can_pause" class="seller-swap-button" type="button" :disabled="listingActionKey === `pause-${row.listing.id}`" @click="runListingAction(row.listing, 'pause')">Pausar</button>
                <button v-if="row.kind === 'listing' && row.listing.safe_actions && row.listing.safe_actions.can_resume" class="seller-swap-button seller-swap-button--primary" type="button" :disabled="listingActionKey === `resume-${row.listing.id}`" @click="runListingAction(row.listing, 'resume')">Reactivar</button>
              </div>
            </div>

            <div v-if="expandedEntryKey === row.key" class="seller-swap-entry-row__details">
              <template v-if="row.kind === 'listing'">
                <div class="seller-swap-proof-strip">
                  <div>
                    <small>Evidencia</small>
                    <strong>{{ proofStatusLabel(row.listing) }}</strong>
                    <span>{{ proofHelper(row.listing) }}</span>
                  </div>
                  <a v-if="row.listing.proof_file_url" :href="row.listing.proof_file_url" target="_blank" rel="noopener">Ver archivo</a>
                </div>

                <div v-if="row.listing.safe_actions && row.listing.safe_actions.can_edit" class="seller-swap-listing-edit">
                  <label>
                    <span>Título</span>
                    <input type="text" :value="listingEditField(row.listing, 'title')" @input="setListingEditField(row.listing, 'title', $event.target.value)" />
                  </label>
                  <label>
                    <span>Ajustar precio USD</span>
                    <input type="number" min="0.01" step="0.01" :value="listingEditField(row.listing, 'unit_price')" @input="setListingEditField(row.listing, 'unit_price', $event.target.value)" />
                  </label>
                  <div v-if="listingSuggestedPrice(row.listing)" class="seller-swap-price-context seller-swap-field--wide">
                    <span>Oficial {{ money(listingOfficialPrice(row.listing), row.listing.currency) }}</span>
                    <strong>Sugerido {{ money(listingSuggestedPrice(row.listing), row.listing.currency) }}</strong>
                    <em>{{ listingPriceSourceLabel(row.listing) }}</em>
                    <button class="seller-swap-button" type="button" @click="setListingEditSuggestedPrice(row.listing)">Volver al sugerido</button>
                  </div>
                  <label>
                    <span>Tipo de entrada</span>
                    <select :value="listingEditField(row.listing, 'delivery_method')" @change="setListingEditField(row.listing, 'delivery_method', $event.target.value)">
                      <option v-for="method in deliveryMethods" :key="method.value" :value="method.value">{{ method.label }}</option>
                    </select>
                  </label>
                  <label v-if="isQrDeliveryMethod(listingEditField(row.listing, 'delivery_method'))">
                    <span>Forma de carga</span>
                    <select :value="listingEditField(row.listing, 'fulfillment_mode')" @change="setListingEditField(row.listing, 'fulfillment_mode', $event.target.value)">
                      <option value="per_unit">Una por entrada</option>
                      <option value="package">Un archivo para todo el paquete</option>
                    </select>
                  </label>
                  <label class="seller-swap-field--wide">
                    <span>Asientos</span>
                    <textarea rows="2" :value="listingEditField(row.listing, 'seat_labels_text')" @input="setListingEditField(row.listing, 'seat_labels_text', $event.target.value)"></textarea>
                  </label>
                  <label class="seller-swap-field--wide">
                    <span>Referencias o códigos</span>
                    <textarea rows="2" :value="listingEditField(row.listing, 'external_refs_text')" placeholder="Una por entrada o una sola si vendes paquete" @input="setListingEditField(row.listing, 'external_refs_text', $event.target.value)"></textarea>
                  </label>
                  <button class="seller-swap-button seller-swap-button--primary seller-swap-field--wide" type="button" :disabled="savingListingEditKey === `listing-${row.listing.id}`" @click="saveListingEdit(row.listing)">{{ savingListingEditKey === `listing-${row.listing.id}` ? 'Guardando...' : 'Guardar cambios' }}</button>
                </div>
                <div v-else-if="row.listing.safe_actions && row.listing.safe_actions.locked_reason" class="seller-swap-delivery-lock">{{ listingLockedLabel(row.listing) }}</div>

                <div v-if="row.listing.safe_actions && row.listing.safe_actions.can_upload_proof" class="seller-swap-proof-form">
                  <label>
                    <span>Archivo o captura</span>
                    <input type="file" @change="setListingProofFile(row.listing, $event)" />
                  </label>
                  <label>
                    <span>Referencia externa</span>
                    <input type="text" :value="listingProofField(row.listing, 'external_reference')" placeholder="Folio, referencia o compra original" @input="setListingProofField(row.listing, 'external_reference', $event.target.value)" />
                  </label>
                  <label class="seller-swap-field--wide">
                    <span>Nota</span>
                    <input type="text" :value="listingProofField(row.listing, 'note')" placeholder="Contexto para recordar esta entrada" @input="setListingProofField(row.listing, 'note', $event.target.value)" />
                  </label>
                  <button class="seller-swap-button seller-swap-button--primary seller-swap-field--wide" type="button" :disabled="uploadingProofKey === `proof-${row.listing.id}` || !canUploadProof(row.listing)" @click="uploadListingProof(row.listing)">{{ uploadingProofKey === `proof-${row.listing.id}` ? 'Subiendo...' : 'Guardar evidencia' }}</button>
                </div>

                <div class="seller-swap-actions-row">
                  <button v-if="row.listing.safe_actions && row.listing.safe_actions.can_cancel" class="seller-swap-button seller-swap-button--danger" type="button" :disabled="listingActionKey === `cancel-${row.listing.id}`" @click="runListingAction(row.listing, 'cancel')">Cancelar entrada</button>
                </div>
              </template>

              <template v-else>
                <div v-if="Array.isArray(row.delivery.artifacts) && row.delivery.artifacts.length" class="seller-swap-artifact-list">
                  <article v-for="artifact in row.delivery.artifacts" :key="artifact.id">
                    <small>{{ artifact.visible_to_buyer ? 'Visible para comprador' : 'Respaldo interno' }} · {{ artifactTypeLabel(artifact.artifact_type) }}</small>
                    <strong>{{ artifact.title || 'Archivo de entrega' }}</strong>
                    <span v-if="artifact.external_reference">{{ artifact.external_reference }}</span>
                    <span v-if="artifact.instructions">{{ artifact.instructions }}</span>
                    <a v-if="artifact.file_url" :href="artifact.file_url" target="_blank" rel="noopener">{{ artifact.original_name || 'Archivo' }}</a>
                  </article>
                </div>

                <div v-if="canSubmitDeliveryHandoff(row.delivery)" class="seller-swap-delivery-form">
                  <label>
                    <span>¿Qué estás entregando?</span>
                    <select :value="handoffField(row.delivery, 'handoff_method')" @change="setHandoffField(row.delivery, 'handoff_method', $event.target.value)">
                      <option v-for="method in handoffMethods" :key="method.value" :value="method.value">{{ method.label }}</option>
                    </select>
                  </label>
                  <label>
                    <span>Referencia</span>
                    <input type="text" :value="handoffField(row.delivery, 'evidence_reference')" placeholder="Folio, enlace o referencia de la entrada" @input="setHandoffField(row.delivery, 'evidence_reference', $event.target.value)" />
                  </label>
                  <label>
                    <span>Archivo, foto o captura</span>
                    <input type="file" @change="setHandoffFile(row.delivery, $event)" />
                  </label>
                  <label>
                    <span>Detalles de entrega</span>
                    <input type="text" :value="handoffField(row.delivery, 'note')" placeholder="Cuenta como fue entregada o que debe saber el comprador" @input="setHandoffField(row.delivery, 'note', $event.target.value)" />
                  </label>
                  <button class="seller-swap-button seller-swap-button--primary" type="button" :disabled="savingDeliveryKey === `delivery-${row.delivery.id}` || !canSubmitDeliveryHandoffForm(row.delivery)" @click="submitDeliveryHandoff(row.delivery)">{{ savingDeliveryKey === `delivery-${row.delivery.id}` ? 'Registrando...' : 'Registrar entrega' }}</button>
                </div>
                <div v-else class="seller-swap-delivery-lock">
                  {{ deliveryLockLabel(row.delivery) }}
                </div>

                <div v-if="messageThreadForDelivery(row.delivery)" class="seller-swap-message-compose seller-swap-message-compose--inline">
                  <label>
                    <span>Mensaje a FOMO</span>
                    <textarea :value="messageDraft(messageThreadForDelivery(row.delivery).id)" rows="2" placeholder="Escribe sobre esta venta" @input="setMessageDraft(messageThreadForDelivery(row.delivery).id, $event.target.value)"></textarea>
                  </label>
                  <button class="seller-swap-button seller-swap-button--primary" type="button" :disabled="sendingMessageKey === `thread-${messageThreadForDelivery(row.delivery).id}` || !messageDraft(messageThreadForDelivery(row.delivery).id)" @click="sendSellerMessage(messageThreadForDelivery(row.delivery))">{{ sendingMessageKey === `thread-${messageThreadForDelivery(row.delivery).id}` ? 'Enviando...' : 'Enviar mensaje' }}</button>
                </div>
              </template>
            </div>
          </article>
        </div>
      </section>

      <section v-if="activeSection === 'finance'" class="seller-swap-panel glass-surface">
        <div class="seller-swap-panel__header">
          <div>
            <span class="workspace-v2__kicker">Pago</span>
            <h2>Tu dinero</h2>
            <p>Consulta ventas verificadas, montos por cobrar, pagos realizados y ajustes.</p>
          </div>
          <button class="seller-swap-button" type="button" :disabled="loading" @click="loadAll">Actualizar</button>
        </div>

        <div class="seller-swap-finance-grid">
          <article v-for="card in financeCards" :key="card.key" class="seller-swap-finance-card">
            <small>{{ card.label }}</small>
            <strong>{{ money(card.value, financeCurrency) }}</strong>
            <span>{{ card.helper }}</span>
          </article>
        </div>

        <div class="seller-swap-finance-split">
          <article class="seller-swap-finance-list">
            <div class="seller-swap-panel__header seller-swap-panel__header--compact">
              <div>
                <span class="workspace-v2__kicker">Últimos movimientos</span>
                <h3>Ventas</h3>
              </div>
            </div>
            <div v-if="!financeRecentAllocations.length" class="seller-swap-empty">Aún no tienes ventas verificadas en Swap.</div>
            <div v-else class="seller-swap-row-list">
              <article v-for="allocation in financeRecentAllocations" :key="allocation.id" class="seller-swap-row-card">
                <div>
                  <strong>{{ allocation.order_number }}</strong>
                  <span>{{ allocation.event_name }} · {{ allocation.listing_title }}</span>
                  <small>{{ allocation.seat_label || 'Asiento pendiente' }}</small>
                </div>
                <div>
                  <span :class="['seller-swap-pill', financeStatusClass(allocation.status)]">{{ financeStatusLabel(allocation.status) }}</span>
                  <strong>{{ money(allocation.seller_payable_amount, allocation.currency) }}</strong>
                </div>
              </article>
            </div>
          </article>

          <article class="seller-swap-finance-list">
            <div class="seller-swap-panel__header seller-swap-panel__header--compact">
              <div>
                <span class="workspace-v2__kicker">Ajustes</span>
                <h3>Reembolsos o reclamos</h3>
              </div>
            </div>
            <div v-if="!financeRecoveries.length" class="seller-swap-empty">No tienes ajustes recientes.</div>
            <div v-else class="seller-swap-row-list">
              <article v-for="recovery in financeRecoveries" :key="recovery.id" class="seller-swap-row-card">
                <div>
                  <strong>{{ recoveryReasonLabel(recovery.reason) }}</strong>
                  <span>{{ formatDate(recovery.created_at) }}</span>
                </div>
                <div>
                  <span :class="['seller-swap-pill', recovery.status === 'open' ? 'is-danger' : 'is-active']">{{ recoveryStatusLabel(recovery.status) }}</span>
                  <strong>{{ money(recovery.amount, recovery.currency) }}</strong>
                </div>
              </article>
            </div>
          </article>
        </div>
      </section>

      <section v-if="activeSection === 'profile'" class="seller-swap-panel glass-surface">
        <div class="seller-swap-panel__header">
          <div>
            <span class="workspace-v2__kicker">Perfil</span>
            <h2>Habilitación inicial</h2>
            <p>Acepta los términos para activar tu perfil. Los compradores no ven tu identidad directa.</p>
          </div>
          <span :class="['seller-swap-pill', profileStatusClass]">{{ profileStatusLabel }}</span>
        </div>

        <div v-if="profileIsLoading" class="seller-swap-empty">Cargando perfil Swap...</div>
        <template v-else>
          <div class="seller-swap-form">
            <label>
              <span>Nombre público</span>
              <input v-model.trim="profileForm.public_name" type="text" placeholder="Vendedor FOMO Quito" />
            </label>
            <label>
              <span>Nombre legal</span>
              <input v-model.trim="profileForm.legal_name" type="text" placeholder="Nombre o razón social" />
            </label>
            <label class="seller-swap-check">
              <input v-model="profileForm.terms_accepted" type="checkbox" :disabled="profile && profile.terms_accepted_at" />
              <span>{{ profile && profile.terms_accepted_at ? 'Términos aceptados' : 'Acepto los términos de FOMO Swap' }}</span>
            </label>
          </div>

          <button class="seller-swap-button seller-swap-button--primary" type="button" :disabled="savingProfile" @click="saveProfile">{{ savingProfile ? 'Guardando...' : 'Guardar perfil' }}</button>
        </template>
      </section>
      </main>
    </div>
  </div>
</template>

<script>
import { apiService, apiServiceAuth, apiServiceAuthForm, apiServiceForm } from '../../common/api.services.js'
import OrganizerWorkspaceNav from '../../components/workspace/OrganizerWorkspaceNav.vue'
import OrganizerWorkspaceTopbar from '../../components/workspace/OrganizerWorkspaceTopbar.vue'

export default {
  name: 'SellerSwapWorkspaceView',

  components: {
    OrganizerWorkspaceNav,
    OrganizerWorkspaceTopbar,
  },

  props: {
    section: {
      type: String,
      default: 'sell',
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    showEmbeddedMenu: {
      type: Boolean,
      default: true,
    },
    authKind: {
      type: String,
      default: 'organizer',
    },
  },
  data() {
    return {
      showNav: false,
      isMobileViewport: typeof window !== 'undefined' ? window.matchMedia('(max-width: 980px)').matches : false,
      loading: false,
      savingProfile: false,
      savingListing: false,
      savingDeliveryKey: '',
      savingListingEditKey: '',
      listingActionKey: '',
      uploadingProofKey: '',
      sendingMessageKey: '',
      embeddedSection: this.section || 'sell',
      entryFilter: 'all',
      expandedEntryKey: '',
      saleStep: 'event',
      pricingOptionsPayload: null,
      pricingOptionsLoading: false,
      feedback: '',
      feedbackType: 'success',
      profile: null,
      market: null,
      events: [],
      listings: [],
      deliveries: [],
      messageThreads: [],
      finance: null,
      deliveryHandoffForms: {},
      messageDrafts: {},
      listingEditForms: {},
      listingProofForms: {},
      profileForm: this.defaultProfileForm(),
      listingForm: this.defaultListingForm(),
    }
  },
  computed: {
    navItems() {
      return [
        {
          key: 'sell',
          label: 'Vender entradas',
          helper: 'Nueva entrada',
          icon: 'fas fa-plus-circle',
          routeName: 'swap-seller-sell',
          title: 'Crea una publicación Swap completa',
          description: 'Precio visible, tipo de entrada y QR/evidencia quedan claros antes de publicar.',
        },
        {
          key: 'tickets',
          label: 'Entradas',
          helper: 'Ventas y entrega',
          icon: 'fas fa-ticket',
          routeName: 'swap-seller-tickets',
          title: 'Entradas y ventas',
          description: 'Gestiona publicación, entrega y mensajes desde una sola lista.',
        },
        {
          key: 'finance',
          label: 'Pago',
          helper: 'Ventas y ajustes',
          icon: 'fas fa-wallet',
          routeName: 'swap-seller-finance',
          title: 'Tu dinero',
          description: 'Consulta ventas verificadas, montos por cobrar, pagos realizados y ajustes.',
        },
        {
          key: 'profile',
          label: 'Perfil',
          helper: 'Habilitación',
          icon: 'fas fa-user-shield',
          routeName: 'swap-seller-profile',
          title: 'Perfil de vendedor',
          description: 'Mantén tus datos y términos listos para vender.',
        },
      ]
    },
    swapSellerHomeRoute() {
      return { name: 'swap-seller-sell' }
    },
    sellerWorkspaceNavItems() {
      const routeName = String((this.$route && this.$route.name) || '')
      return [
        {
          label: 'FOMO Swap',
          icon: 'fas fa-right-left',
          to: this.swapSellerHomeRoute,
          active: routeName === 'swap-seller' || routeName.startsWith('swap-seller-'),
        },
      ]
    },
    sellerContextNavItems() {
      return this.navItems.map((item) => ({
        label: item.label,
        icon: item.icon,
        to: { name: item.routeName },
        active: item.key === this.activeSection,
      }))
    },
    sellerNavContextLabel() {
      if (this.profileStatusLabel && this.profileStatusLabel !== 'Sin perfil') {
        return this.profileStatusLabel
      }
      return 'FOMO Swap'
    },
    activeSection() {
      if (this.embedded) {
        const embeddedValue = this.embeddedSection || 'sell'
        if (['deliveries', 'messages'].includes(embeddedValue)) return 'tickets'
        return this.navItems.some((item) => item.key === embeddedValue) ? embeddedValue : 'sell'
      }
      const value = this.section || (this.$route && this.$route.params && this.$route.params.section) || 'sell'
      if (['deliveries', 'messages'].includes(value)) return 'tickets'
      return this.navItems.some((item) => item.key === value) ? value : 'sell'
    },
    activeSectionConfig() {
      return this.navItems.find((item) => item.key === this.activeSection) || this.navItems[0]
    },
    profileIsLoading() {
      return this.loading && !this.profile
    },
    profileNeedsCompletion() {
      return !this.profileIsLoading && (!this.profile || !this.profile.terms_accepted_at)
    },
    profileStatusLabel() {
      if (this.loading && !this.profile) return 'Cargando'
      if (!this.profile) return 'Sin perfil'
      const labels = {
        active: 'Activo',
        pending: 'Pendiente',
        review: 'Con revisión',
        pending_review: 'Con revisión',
        blocked: 'Bloqueado',
        rejected: 'Requiere cambios',
        inactive: 'Inactivo',
      }
      return labels[this.profile.status] || this.humanizeStatus(this.profile.status)
    },
    profileStatusClass() {
      if (this.loading && !this.profile) return 'is-warning'
      if (!this.profile) return 'is-muted'
      if (this.profile.status === 'active') return 'is-active'
      if (this.profile.status === 'blocked') return 'is-danger'
      return 'is-warning'
    },
    heroActionLabel() {
      if (this.profileIsLoading) return 'Cargando'
      if (this.profileNeedsCompletion) return 'Completar perfil'
      if (this.activeSection === 'sell') return 'Ver entradas'
      return 'Actualizar datos'
    },
    heroActionIcon() {
      if (this.profileIsLoading) return 'fas fa-rotate'
      if (this.profileNeedsCompletion) return 'fas fa-user-shield'
      if (this.activeSection === 'sell') return 'fas fa-ticket'
      return 'fas fa-rotate'
    },
    heroActionDisabled() {
      return this.loading
    },
    canSubmitListing() {
      return Boolean(
        this.profile
        && this.profile.terms_accepted_at
        && this.listingForm.event
        && this.listingForm.official_ticket
        && this.listingForm.quantity
        && this.listingForm.unit_price
        && !this.priceBelowOfficialBlocked
        && this.deliveryEvidenceReady
      )
    },
    sellSteps() {
      return [
        { key: 'event', label: 'Evento' },
        { key: 'category', label: 'Tipo' },
        { key: 'quantity', label: 'Cantidad' },
        { key: 'price', label: 'Precio' },
        { key: 'delivery', label: 'Entrega' },
        { key: 'publish', label: 'Publicar' },
      ]
    },
    sellProgress() {
      const index = this.sellSteps.findIndex((step) => step.key === this.saleStep)
      return Math.max(12, Math.round(((index + 1) / this.sellSteps.length) * 100))
    },
    selectedEvent() {
      return this.events.find((event) => Number(event.id) === Number(this.listingForm.event)) || null
    },
    pricingOptions() {
      return this.pricingOptionsPayload && Array.isArray(this.pricingOptionsPayload.options) ? this.pricingOptionsPayload.options : []
    },
    selectedPricingOption() {
      return this.pricingOptions.find((option) => Number(option.id) === Number(this.listingForm.official_ticket)) || null
    },
    suggestedPrice() {
      return this.selectedPricingOption ? Number(this.selectedPricingOption.recommended_price || 0) : 0
    },
    officialPrice() {
      return this.selectedPricingOption ? Number(this.selectedPricingOption.official_price || 0) : 0
    },
    marketReferencePrice() {
      if (!this.selectedPricingOption) return 0
      const market = Number(this.selectedPricingOption.market_price || 0)
      return market > 0 ? market : Number(this.selectedPricingOption.recommended_price || 0)
    },
    minimumAllowedPrice() {
      return this.selectedPricingOption ? Number(this.selectedPricingOption.minimum_allowed_price || 0) : 0
    },
    priceBelowOfficialBlocked() {
      if (!this.selectedPricingOption || this.selectedPricingOption.allow_below_official_price) return false
      return Number(this.listingForm.unit_price || 0) > 0 && Number(this.listingForm.unit_price || 0) < this.minimumAllowedPrice
    },
    pricePolicyWarning() {
      if (!this.selectedPricingOption) return ''
      if (this.priceBelowOfficialBlocked) return `El precio mínimo para esta categoría es el precio oficial: ${this.money(this.minimumAllowedPrice, 'USD')}.`
      if (this.selectedPricingOption.allow_below_official_price && Number(this.listingForm.unit_price || 0) < this.officialPrice) return 'FOMO permite publicar bajo precio oficial para este evento, pero el sugerido se mantiene como referencia.'
      return ''
    },
    quantityChoices() {
      if (this.selectedPricingOption && this.selectedPricingOption.is_package) return [1]
      return [1, 2, 3, 4, 5]
    },
    estimatedSellerTotal() {
      return Number(this.listingForm.quantity || 0) * Number(this.listingForm.unit_price || this.suggestedPrice || 0)
    },
    externalRefLines() {
      return this.parseLines(this.listingForm.external_refs_text)
    },
    externalRefsReadyForUnits() {
      const count = this.externalRefLines.length
      if (!count) return false
      if (this.listingForm.fulfillment_mode === 'package') return count === 1
      return count === Number(this.listingForm.quantity || 0)
    },
    deliveryEvidenceRequired() {
      return this.isQrDeliveryMethod(this.listingForm.delivery_method)
    },
    deliveryEvidenceReady() {
      if (!this.deliveryEvidenceRequired) return true
      return Boolean(this.listingForm.proof_file || this.externalRefsReadyForUnits)
    },
    livePreviewProofClass() {
      if (this.deliveryEvidenceReady) return 'is-ready'
      if (!this.selectedPricingOption || !['delivery', 'publish'].includes(this.saleStep)) return 'is-neutral'
      return ''
    },
    livePreviewProofIcon() {
      if (this.deliveryEvidenceReady) return 'fas fa-circle-check'
      if (!this.selectedPricingOption || !['delivery', 'publish'].includes(this.saleStep)) return 'fas fa-info-circle'
      return 'fas fa-triangle-exclamation'
    },
    livePreviewProofLabel() {
      if (!this.selectedPricingOption) return 'Elige tipo de entrada'
      if (!this.deliveryEvidenceRequired) return 'Entrega coordinada'
      if (this.deliveryEvidenceReady) return 'QR/evidencia lista'
      if (!['delivery', 'publish'].includes(this.saleStep)) return 'QR se pedirá antes de publicar'
      return 'Falta QR o referencia'
    },
    deliveryPrimaryProofLabel() {
      if (this.listingForm.delivery_method === 'qr_redeem_physical') return 'Sube el QR canjeable o temporal'
      return 'Sube el QR final de la entrada'
    },
    deliveryProofHelper() {
      if (this.listingForm.delivery_method === 'qr_redeem_physical') {
        return 'Este QR permite retirar o canjear una entrada física. Déjalo claro para que FOMO y el comprador sepan que no es el acceso final directo.'
      }
      return 'Este debe ser el QR o PDF final de acceso. FOMO lo resguarda y lo libera al comprador solo después de verificar el pago.'
    },
    listingProofDraftFileName() {
      return this.listingForm.proof_file ? this.listingForm.proof_file.name : 'Subir QR, PDF o captura'
    },
    sellerReferencePlaceholder() {
      if (this.listingForm.fulfillment_mode === 'package') return 'Una referencia del paquete completo si no subes archivo'
      return 'Una referencia por entrada si no subes archivo\nEj: QR-001\nQR-002'
    },
    referenceCountHint() {
      if (this.listingForm.proof_file) return 'Si subes archivo, la referencia ayuda a FOMO pero no es obligatoria.'
      if (this.listingForm.fulfillment_mode === 'package') return this.externalRefsReadyForUnits ? 'Referencia de paquete lista.' : 'Necesitas 1 referencia si no subes archivo.'
      const quantity = Number(this.listingForm.quantity || 0)
      const count = this.externalRefLines.length
      return this.externalRefsReadyForUnits ? `${count} referencia${count === 1 ? '' : 's'} lista${count === 1 ? '' : 's'}.` : `Necesitas ${quantity} referencia${quantity === 1 ? '' : 's'} si no subes archivo.`
    },
    publishPreviewTitle() {
      return (this.selectedPricingOption && this.selectedPricingOption.name) || this.listingForm.title || 'Tu entrada FOMO Swap'
    },
    financeCurrency() {
      return (this.finance && this.finance.currency) || 'USD'
    },
    financeSummary() {
      return (this.finance && this.finance.summary) || {}
    },
    financeCards() {
      const summary = this.financeSummary
      return [
        { key: 'estimated', label: 'Ventas', value: summary.estimated, helper: 'Entradas vendidas o reservadas' },
        { key: 'held', label: 'Por cobrar', value: summary.held, helper: 'Esperando entrega o periodo de seguridad' },
        { key: 'available', label: 'Disponible', value: summary.available, helper: 'Listo para pago' },
        { key: 'reserved_for_payout', label: 'Pago en proceso', value: summary.reserved_for_payout, helper: 'FOMO ya lo está preparando' },
        { key: 'paid', label: 'Pagado', value: summary.paid, helper: 'Transferido' },
        { key: 'open_recovery', label: 'Ajustes', value: summary.open_recovery, helper: 'Reembolsos o reclamos' },
      ]
    },
    financeRecentAllocations() {
      return this.finance && Array.isArray(this.finance.recent_allocations) ? this.finance.recent_allocations : []
    },
    financeRecoveries() {
      return this.finance && Array.isArray(this.finance.recoveries) ? this.finance.recoveries : []
    },
    deliveryMethods() {
      return [
        { value: 'physical_handoff', label: 'Entrega física' },
        { value: 'physical_shipping', label: 'Envío físico' },
        { value: 'app_transfer', label: 'Transferido por app' },
        { value: 'qr_final_ticket', label: 'Envío QR (entrada final)' },
        { value: 'qr_redeem_physical', label: 'Envío QR (debe canjear luego físico)' },
      ]
    },
    handoffMethods() {
      return [
        { value: 'physical_handoff', label: 'Entrega física' },
        { value: 'physical_shipping', label: 'Envío físico' },
        { value: 'app_transfer', label: 'Transferido por app' },
      ]
    },
    deliveryListingIds() {
      return new Set(this.deliveries.map((delivery) => delivery && delivery.item && delivery.item.listing).filter(Boolean))
    },
    entryRows() {
      const deliveryRows = this.deliveries.map((delivery) => this.deliveryEntryRow(delivery)).filter(Boolean)
      const listingRows = this.listings
        .filter((listing) => !this.deliveryListingIds.has(listing.id) || this.listingAvailableQuantity(listing) > 0)
        .map((listing) => this.listingEntryRow(listing))
        .filter(Boolean)
      return [...deliveryRows, ...listingRows]
    },
    filteredEntryRows() {
      if (this.entryFilter === 'all') return this.entryRows
      return this.entryRows.filter((row) => row.filterKeys.includes(this.entryFilter))
    },
    entryFilters() {
      const filters = [
        { key: 'all', label: 'Todas' },
        { key: 'published', label: 'Publicadas' },
        { key: 'sold', label: 'Vendidas' },
        { key: 'to_deliver', label: 'Por entregar' },
        { key: 'delivered', label: 'Entregadas' },
        { key: 'issue', label: 'Con problema' },
      ]
      return filters.map((filter) => ({
        ...filter,
        count: filter.key === 'all' ? this.entryRows.length : this.entryRows.filter((row) => row.filterKeys.includes(filter.key)).length,
      }))
    },
  },
  created() {
    this.loadAll()
  },
  mounted() {
    this.handleViewportChange = () => {
      this.isMobileViewport = window.matchMedia('(max-width: 980px)').matches
      if (!this.isMobileViewport) this.showNav = false
    }
    this.handleViewportChange()
    window.addEventListener('resize', this.handleViewportChange)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleViewportChange)
  },
  watch: {
    section(value) {
      if (this.embedded && value) this.embeddedSection = value
    },
  },
  methods: {
    setEmbeddedSection(section) {
      this.embeddedSection = section
    },
    swapApi(endpoint, method, data, options = {}) {
      if (this.authKind === 'ticket') return apiServiceAuth(endpoint, method, data, undefined, 'ticket', options)
      return apiService(endpoint, method, data, options)
    },
    swapApiForm(endpoint, method, data, options = {}) {
      if (this.authKind === 'ticket') return apiServiceAuthForm(endpoint, method, data, undefined, 'ticket', options)
      return apiServiceForm(endpoint, method, data, options)
    },
    defaultProfileForm() {
      return { public_name: '', legal_name: '', terms_accepted: false, email_notifications_enabled: true }
    },
    defaultListingForm() {
      return {
        event: '',
        official_ticket: '',
        title: '',
        section_label: '',
        row_label: '',
        quantity: 1,
        unit_price: '',
        currency: 'USD',
        delivery_method: 'qr_final_ticket',
        fulfillment_mode: 'per_unit',
        seats_together: true,
        seat_labels_text: '',
        external_refs_text: '',
        proof_file: null,
        proof_note: '',
      }
    },
    quantityLabel(quantity) {
      const value = Number(quantity || 0)
      if (this.selectedPricingOption && this.selectedPricingOption.is_package) return `${this.selectedPricingOption.name} completo`
      return `${value} ${value === 1 ? 'entrada' : 'entradas'}`
    },
    categoryKindLabel(option) {
      if (!option) return 'Categoría oficial'
      if (option.is_package) return 'Paquete completo'
      const name = String(option.name || '').toLowerCase()
      if (name.includes('vip') || name.includes('palco') || name.includes('mesa') || name.includes('fila')) return 'Entrada numerada o premium'
      return 'Entrada individual'
    },
    categoryIcon(option) {
      if (!option) return 'fas fa-ticket-alt'
      const name = String(option.name || '').toLowerCase()
      if (option.is_package) return 'fas fa-layer-group'
      if (name.includes('vip') || name.includes('palco')) return 'fas fa-crown'
      if (name.includes('mesa')) return 'fas fa-glass-cheers'
      if (name.includes('fila') || name.includes('seat') || name.includes('silla')) return 'fas fa-chair'
      return 'fas fa-ticket-alt'
    },
    categoryCardCopy(option) {
      if (!option) return 'Elige una categoría oficial para continuar.'
      const max = Number(option.max_quantity || option.quantity || 1)
      const base = option.description || `Máximo sugerido por compra: ${max} ${max === 1 ? 'entrada' : 'entradas'}.`
      return `${base} Precio oficial ${this.money(option.official_price, option.currency)}.`
    },
    sellStepIsDone(key) {
      if (key === 'event') return Boolean(this.listingForm.event)
      if (key === 'category') return Boolean(this.listingForm.official_ticket)
      if (key === 'quantity') return Boolean(this.listingForm.quantity)
      if (key === 'price') return Boolean(this.listingForm.unit_price) && !this.priceBelowOfficialBlocked
      if (key === 'delivery') return Boolean(this.listingForm.delivery_method) && this.deliveryEvidenceReady
      if (key === 'publish') return this.canSubmitListing
      return false
    },
    goSaleStep(step) {
      this.saleStep = step
    },
    async selectListingEvent(event) {
      if (!event || !event.id) return
      this.listingForm.event = event.id
      this.listingForm.official_ticket = ''
      this.listingForm.title = ''
      this.listingForm.section_label = ''
      this.listingForm.unit_price = ''
      await this.loadPricingOptions(event.id)
      this.saleStep = 'category'
    },
    async loadPricingOptions(eventId) {
      if (!eventId) {
        this.pricingOptionsPayload = null
        return
      }
      this.pricingOptionsLoading = true
      const data = await this.swapApi(`/api/swap/seller/events/${encodeURIComponent(eventId)}/pricing-options/`)
      this.pricingOptionsLoading = false
      if (data && Array.isArray(data.options) && !data.err) {
        this.pricingOptionsPayload = data
        return
      }
      this.pricingOptionsPayload = null
      this.feedback = this.extractError(data, 'No pudimos calcular precios sugeridos para este evento.')
      this.feedbackType = 'error'
    },
    selectPricingOption(option) {
      if (!option || !option.id) return
      this.listingForm.official_ticket = option.id
      this.listingForm.title = option.name
      this.listingForm.section_label = option.name
      this.listingForm.quantity = option.is_package ? 1 : Math.max(1, Number(this.listingForm.quantity || 1))
      this.listingForm.fulfillment_mode = option.is_package ? 'package' : 'per_unit'
      this.listingForm.unit_price = option.recommended_price
      this.saleStep = 'quantity'
    },
    setListingQuantity(quantity) {
      this.listingForm.quantity = Number(quantity || 1)
    },
    useSuggestedPrice() {
      if (!this.selectedPricingOption) return
      this.listingForm.unit_price = this.selectedPricingOption.recommended_price
    },
    setListingDraftProofFile(event) {
      const file = event && event.target && event.target.files && event.target.files[0] ? event.target.files[0] : null
      this.listingForm.proof_file = file
    },
    listingExternalRefsForPayload() {
      const refs = this.externalRefLines
      if (!refs.length) return []
      if (this.listingForm.fulfillment_mode === 'package') return refs.length === 1 ? refs : []
      return refs.length === Number(this.listingForm.quantity || 0) ? refs : []
    },
    deliveryChoiceCopy(method) {
      const copy = {
        physical_handoff: 'La entrega se coordina solo cuando el pago esté confirmado.',
        physical_shipping: 'Envías físicamente y luego registras guía o evidencia.',
        app_transfer: 'La transfieres en una app externa y guardas la referencia.',
        qr_final_ticket: 'Es el archivo final de acceso. Súbelo ahora para quedar listo.',
        qr_redeem_physical: 'Es un QR temporal o canjeable; el comprador debe retirar físico después.',
      }
      return copy[method] || 'Entrega protegida por FOMO.'
    },
    deliveryChoiceBadge(method) {
      const badges = {
        physical_handoff: 'Entrega coordinada',
        physical_shipping: 'Requiere envío',
        app_transfer: 'Transferencia externa',
        qr_final_ticket: 'QR final',
        qr_redeem_physical: 'QR temporal/canje',
      }
      return badges[method] || 'Protegida'
    },
    deliveryMethodIcon(method) {
      const icons = {
        physical_handoff: 'fas fa-handshake',
        physical_shipping: 'fas fa-truck',
        app_transfer: 'fas fa-mobile-alt',
        qr_final_ticket: 'fas fa-qrcode',
        qr_redeem_physical: 'fas fa-ticket-alt',
      }
      return icons[method] || 'fas fa-ticket-alt'
    },
    formatDate(value) {
      if (!value) return 'Fecha pendiente'
      try { return new Intl.DateTimeFormat('es-EC', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) } catch (_error) { return value }
    },
    money(value, currency = 'USD') {
      const amount = Number(value || 0)
      try { return new Intl.NumberFormat('es-EC', { style: 'currency', currency: currency || 'USD' }).format(amount) } catch (_error) { return `${amount.toFixed(2)} ${currency || 'USD'}` }
    },
    listingAvailableQuantity(listing) {
      const availability = listing && listing.availability ? listing.availability : null
      if (availability && Number.isFinite(Number(availability.available_units))) return Number(availability.available_units)
      return Number((listing && listing.quantity) || 0)
    },
    proofStatusClass(listing) {
      const status = listing && listing.proof_status
      if (status === 'approved') return 'is-active'
      if (status === 'rejected') return 'is-danger'
      return 'is-warning'
    },
    listingEntryRow(listing) {
      if (!listing || !listing.id) return null
      const available = this.listingAvailableQuantity(listing)
      const filterKeys = ['published']
      if (['rejected', 'blocked', 'cancelled'].includes(listing.status)) filterKeys.push('issue')
      return {
        key: `listing-${listing.id}`,
        kind: 'listing',
        listing,
        title: listing.title || listing.event_name || 'Entrada',
        subtitle: [listing.event_name, [listing.section_label, listing.row_label].filter(Boolean).join(' · ')].filter(Boolean).join(' · ') || 'Zona pendiente',
        seat: this.seatSummary(listing),
        eventDate: this.formatDate(listing.event_start_date),
        primaryStatusLabel: this.listingStatusLabel(listing.status),
        primaryStatusClass: this.listingStatusClass(listing.status),
        secondaryStatusLabel: this.proofStatusLabel(listing),
        secondaryStatusClass: this.proofStatusClass(listing),
        moneyKicker: available === 1 ? '1 disponible' : `${available || listing.quantity || 0} disponibles`,
        amountLabel: `${available || listing.quantity || 0} x ${this.money(listing.unit_price, listing.currency)}`,
        moneyHelper: this.deliveryMethodLabel(listing.delivery_method),
        primaryActionLabel: 'Ajustar precio',
        filterKeys,
      }
    },
    deliveryEntryRow(delivery) {
      if (!delivery || !delivery.id) return null
      const item = delivery.item || {}
      const filterKeys = ['sold']
      if (['pending_seller_action', 'seller_confirmed', 'delivery_in_progress', 'claim_window_open'].includes(delivery.status)) filterKeys.push('to_deliver')
      if (['delivered', 'buyer_accessible', 'released'].includes(delivery.status)) filterKeys.push('delivered')
      if (['cancelled', 'disputed', 'replaced'].includes(delivery.status) || ['failed', 'refunded', 'chargeback'].includes(delivery.order_payment_status)) filterKeys.push('issue')
      return {
        key: `delivery-${delivery.id}`,
        kind: 'delivery',
        delivery,
        title: this.deliveryItemTitle(delivery),
        subtitle: [delivery.event_name, this.deliveryMethodLabel(delivery.delivery_method)].filter(Boolean).join(' · '),
        seat: this.deliverySeatSummary(delivery),
        orderNumber: delivery.order_number,
        primaryStatusLabel: 'Vendida',
        primaryStatusClass: 'is-active',
        secondaryStatusLabel: this.deliveryStatusLabel(delivery.status),
        secondaryStatusClass: this.deliveryStatusClass(delivery.status),
        moneyKicker: `Pago ${this.paymentStatusLabel(delivery.order_payment_status)}`,
        amountLabel: this.money(item.unit_price, item.currency || 'USD'),
        moneyHelper: delivery.order_number,
        primaryActionLabel: this.canSubmitDeliveryHandoff(delivery) ? 'Anunciar entrega' : 'Entrega y mensaje',
        filterKeys,
      }
    },
    toggleEntryDetails(key) {
      this.expandedEntryKey = this.expandedEntryKey === key ? '' : key
    },
    messageThreadForDelivery(delivery) {
      if (!delivery) return null
      const orderItemId = delivery.item && delivery.item.order_item ? Number(delivery.item.order_item) : null
      return this.messageThreads.find((thread) => {
        const threadOrderItemId = thread && thread.order_item ? Number(thread.order_item) : null
        if (orderItemId && threadOrderItemId && orderItemId === threadOrderItemId) return true
        return thread && delivery.order_number && thread.order_number === delivery.order_number
      }) || null
    },
    handleHeroAction() {
      if (this.profileNeedsCompletion) {
        if (this.embedded) {
          this.setEmbeddedSection('profile')
          return
        }
        this.$router.push({ name: 'swap-seller-profile' })
        return
      }
      if (this.activeSection === 'sell') {
        if (this.embedded) {
          this.setEmbeddedSection('tickets')
          return
        }
        this.$router.push({ name: 'swap-seller-tickets' })
        return
      }
      this.loadAll()
    },
    extractError(payload, fallback) {
      if (!payload) return fallback
      if (payload.err) return payload.err
      if (payload.detail) return Array.isArray(payload.detail) ? payload.detail.join(', ') : payload.detail
      const firstValue = typeof payload === 'object' ? Object.values(payload)[0] : null
      if (Array.isArray(firstValue)) return firstValue[0]
      return firstValue || fallback
    },
    parseLines(value) {
      return String(value || '').split(/[\n,]+/).map((item) => item.trim()).filter(Boolean)
    },
    humanizeStatus(value) {
      if (!value) return 'Pendiente'
      return String(value).replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    },
    listingStatusLabel(status) {
      const labels = {
        draft: 'Borrador',
        pending_review: 'Con revisión',
        approved: 'Publicada',
        paused: 'Pausada',
        rejected: 'Requiere cambios',
        blocked: 'Bloqueada',
        cancelled: 'Cancelada',
        sold_out: 'Vendida',
      }
      return labels[status] || this.humanizeStatus(status)
    },
    deliveryStatusLabel(status) {
      const labels = {
        pending_seller_action: 'Por entregar',
        seller_confirmed: 'Entrega registrada',
        delivery_in_progress: 'En proceso',
        delivered: 'Entregada',
        buyer_accessible: 'Lista para comprador',
        claim_window_open: 'En periodo de seguridad',
        released: 'Completada',
        cancelled: 'Cancelada',
        disputed: 'Con reclamo',
        replaced: 'Reemplazada',
      }
      return labels[status] || this.humanizeStatus(status)
    },
    paymentStatusLabel(status) {
      const labels = {
        pending: 'pendiente',
        manual_review: 'en revisión',
        verified: 'confirmado',
        failed: 'fallido',
        refunded: 'reembolsado',
        chargeback: 'con reclamo',
      }
      return labels[status] || String(this.humanizeStatus(status)).toLowerCase()
    },
    financeStatusLabel(status) {
      const labels = {
        pending: 'Pendiente',
        delivery_pending: 'Por entregar',
        delivered: 'Entregado',
        held: 'Por cobrar',
        releasable: 'Disponible',
        reserved_for_payout: 'Pago en proceso',
        paid: 'Pagado',
        disputed: 'Con reclamo',
        refunded: 'Reembolsado',
        cancelled: 'Cancelado',
      }
      return labels[status] || this.humanizeStatus(status)
    },
    recoveryStatusLabel(status) {
      const labels = {
        open: 'Abierto',
        closed: 'Cerrado',
        waived: 'Cerrado',
      }
      return labels[status] || this.humanizeStatus(status)
    },
    recoveryReasonLabel(reason) {
      const normalized = String(reason || '').toLowerCase()
      if (normalized.includes('chargeback')) return 'Ajuste por reclamo'
      if (normalized.includes('refund')) return 'Ajuste por reembolso'
      return reason ? this.humanizeStatus(reason) : 'Ajuste de venta'
    },
    deliveryMethodLabel(method) {
      const labels = {
        physical_handoff: 'Entrega física',
        physical_shipping: 'Envío físico',
        app_transfer: 'Transferido por app',
        qr_final_ticket: 'Envío QR (entrada final)',
        qr_redeem_physical: 'Envío QR (debe canjear luego físico)',
        coordinated: 'Entrega física',
        upload: 'Envío QR (entrada final)',
        external_transfer: 'Transferido por app',
      }
      return labels[method] || this.humanizeStatus(method)
    },
    artifactTypeLabel(type) {
      const labels = {
        ticket_file: 'Archivo de entrada',
        transfer_reference: 'Referencia',
        delivery_note: 'Nota de entrega',
        refund_proof: 'Comprobante',
      }
      return labels[type] || this.humanizeStatus(type)
    },
    messageSenderLabel(role) {
      const labels = {
        system: 'FOMO',
        admin: 'FOMO',
        seller: 'Tú',
        buyer: 'Comprador',
      }
      return labels[role] || this.humanizeStatus(role)
    },
    messageThreadStatusLabel(status) {
      const labels = {
        open: 'Abierto',
        active: 'Activo',
        closed: 'Cerrado',
      }
      return labels[status] || this.humanizeStatus(status)
    },
    listingStatusClass(status) {
      if (status === 'approved') return 'is-active'
      if (['rejected', 'blocked', 'cancelled'].includes(status)) return 'is-danger'
      return 'is-warning'
    },
    deliveryStatusClass(status) {
      if (['seller_confirmed', 'delivery_in_progress', 'delivered', 'buyer_accessible', 'claim_window_open', 'released'].includes(status)) return 'is-active'
      if (['cancelled', 'disputed', 'replaced'].includes(status)) return 'is-danger'
      return 'is-warning'
    },
    paymentStatusClass(status) {
      if (status === 'verified') return 'is-active'
      if (['failed', 'refunded', 'chargeback'].includes(status)) return 'is-danger'
      return 'is-warning'
    },
    financeStatusClass(status) {
      if (['delivered', 'releasable', 'paid'].includes(status)) return 'is-active'
      if (['disputed', 'refunded', 'cancelled'].includes(status)) return 'is-danger'
      return 'is-warning'
    },
    seatSummary(listing) {
      const seats = Array.isArray(listing.units) ? listing.units.map((unit) => unit.seat_label).filter(Boolean) : []
      if (seats.length) return seats.join(', ')
      return listing.seats_together ? 'Asientos juntos' : 'Asientos no juntos'
    },
    unitSeatText(listing) {
      return Array.isArray(listing && listing.units) ? listing.units.map((unit) => unit.seat_label).filter(Boolean).join(', ') : ''
    },
    unitExternalRefText(listing) {
      return Array.isArray(listing && listing.units) ? listing.units.map((unit) => unit.external_ticket_ref).filter(Boolean).join('\n') : ''
    },
    listingPricingContext(listing) {
      return (listing && listing.pricing_context) || {}
    },
    listingSuggestedPrice(listing) {
      return this.listingPricingContext(listing).recommended_price || ''
    },
    listingOfficialPrice(listing) {
      const context = this.listingPricingContext(listing)
      const officialTicket = listing && listing.official_ticket ? listing.official_ticket : {}
      return context.official_price || officialTicket.official_price || ''
    },
    listingPriceSourceLabel(listing) {
      return this.listingPricingContext(listing).recommended_price_source_label || 'Precio sugerido FOMO'
    },
    setListingEditSuggestedPrice(listing) {
      const suggestedPrice = this.listingSuggestedPrice(listing)
      if (suggestedPrice) this.setListingEditField(listing, 'unit_price', suggestedPrice)
    },
    isQrDeliveryMethod(method) {
      return ['qr_final_ticket', 'qr_redeem_physical'].includes(method)
    },
    proofStatusLabel(listing) {
      const labels = {
        missing: 'Sin evidencia',
        submitted: 'Evidencia guardada',
        pending_review: 'Guardada',
        approved: 'Guardada',
        rejected: 'Rechazada',
      }
      const status = listing && listing.proof_status
      return labels[status] || (status ? this.humanizeStatus(status) : 'Sin evidencia')
    },
    proofHelper(listing) {
      const proof = (listing && listing.proof_metadata) || {}
      return proof.note || proof.external_reference || 'Puedes guardar archivo, foto o referencia para respaldar esta entrada.'
    },
    listingLockedLabel(listing) {
      const reason = listing && listing.safe_actions ? String(listing.safe_actions.locked_reason || '') : ''
      if (reason.toLowerCase().includes('delivery') || reason.toLowerCase().includes('hold') || reason.toLowerCase().includes('venta')) {
        return 'Esta entrada ya tiene una venta o entrega en curso.'
      }
      return reason || 'Esta entrada no se puede editar ahora.'
    },
    deliveryItemTitle(delivery) {
      const item = delivery && delivery.item ? delivery.item : {}
      return item.listing_title || [item.section_label, item.row_label].filter(Boolean).join(' · ') || 'Entrada'
    },
    deliverySeatSummary(delivery) {
      const item = delivery && delivery.item ? delivery.item : {}
      return [item.section_label, item.row_label, item.seat_label].filter(Boolean).join(' · ') || 'Asiento pendiente'
    },
    canSubmitDeliveryHandoff(delivery) {
      return Boolean(delivery && !this.isQrDeliveryMethod(delivery.delivery_method) && delivery.order_payment_status === 'verified' && delivery.status === 'pending_seller_action')
    },
    canSubmitDeliveryHandoffForm(delivery) {
      if (!this.canSubmitDeliveryHandoff(delivery)) return false
      const form = this.deliveryHandoffForms[(delivery && delivery.id) || ''] || {}
      return Boolean(form.file || String(form.evidence_reference || '').trim() || String(form.note || '').trim())
    },
    deliveryLockLabel(delivery) {
      if (!delivery) return 'Entrega pendiente.'
      if (this.isQrDeliveryMethod(delivery.delivery_method) && delivery.status === 'buyer_accessible') return 'QR liberado al comprador.'
      if (this.isQrDeliveryMethod(delivery.delivery_method)) return 'El QR se libera automáticamente cuando FOMO verifica el pago.'
      if (delivery.order_payment_status !== 'verified') return 'FOMO aún no verifica el pago; espera antes de entregar.'
      if (delivery.status === 'seller_confirmed') return 'Entrega registrada.'
      return 'Esta entrega ya está en una etapa posterior.'
    },
    syncDeliveryHandoffForms() {
      const forms = {}
      this.deliveries.forEach((delivery) => {
        const existing = this.deliveryHandoffForms[delivery.id] || {}
        const handoff = (delivery.metadata && delivery.metadata.seller_handoff) || {}
        forms[delivery.id] = {
          handoff_method: Object.prototype.hasOwnProperty.call(existing, 'handoff_method') ? existing.handoff_method : (handoff.handoff_method || 'physical_handoff'),
          evidence_reference: Object.prototype.hasOwnProperty.call(existing, 'evidence_reference') ? existing.evidence_reference : (handoff.evidence_reference || ''),
          note: Object.prototype.hasOwnProperty.call(existing, 'note') ? existing.note : (handoff.note || ''),
          file: Object.prototype.hasOwnProperty.call(existing, 'file') ? existing.file : null,
        }
      })
      this.deliveryHandoffForms = forms
    },
    syncListingForms() {
      const editForms = {}
      const proofForms = {}
      this.listings.forEach((listing) => {
        const existingEdit = this.listingEditForms[listing.id] || {}
        editForms[listing.id] = {
          title: Object.prototype.hasOwnProperty.call(existingEdit, 'title') ? existingEdit.title : (listing.title || ''),
          unit_price: Object.prototype.hasOwnProperty.call(existingEdit, 'unit_price') ? existingEdit.unit_price : (listing.unit_price || ''),
          delivery_method: Object.prototype.hasOwnProperty.call(existingEdit, 'delivery_method') ? existingEdit.delivery_method : (listing.delivery_method || 'qr_final_ticket'),
          fulfillment_mode: Object.prototype.hasOwnProperty.call(existingEdit, 'fulfillment_mode') ? existingEdit.fulfillment_mode : (listing.fulfillment_mode || (listing.metadata && listing.metadata.fulfillment_mode) || 'per_unit'),
          seat_labels_text: Object.prototype.hasOwnProperty.call(existingEdit, 'seat_labels_text') ? existingEdit.seat_labels_text : this.unitSeatText(listing),
          external_refs_text: Object.prototype.hasOwnProperty.call(existingEdit, 'external_refs_text') ? existingEdit.external_refs_text : this.unitExternalRefText(listing),
        }
        proofForms[listing.id] = {
          file: null,
          external_reference: ((listing.proof_metadata || {}).external_reference) || '',
          note: '',
        }
      })
      this.listingEditForms = editForms
      this.listingProofForms = proofForms
    },
    handoffField(delivery, field) {
      const form = this.deliveryHandoffForms[(delivery && delivery.id) || ''] || {}
      return Object.prototype.hasOwnProperty.call(form, field) ? form[field] : ''
    },
    setHandoffField(delivery, field, value) {
      if (!delivery || !delivery.id) return
      this.deliveryHandoffForms = {
        ...this.deliveryHandoffForms,
        [delivery.id]: {
          ...(this.deliveryHandoffForms[delivery.id] || {}),
          [field]: value,
        },
      }
    },
    setHandoffFile(delivery, event) {
      const file = event && event.target && event.target.files && event.target.files[0] ? event.target.files[0] : null
      this.setHandoffField(delivery, 'file', file)
    },
    syncProfileForm() {
      this.profileForm = {
        public_name: (this.profile && this.profile.public_name) || '',
        legal_name: (this.profile && this.profile.legal_name) || '',
        email_notifications_enabled: this.profile ? this.profile.email_notifications_enabled : true,
        terms_accepted: Boolean(this.profile && this.profile.terms_accepted_at),
      }
    },
    async loadAll(options = {}) {
      this.loading = true
      if (!options.preserveFeedback) this.feedback = ''
      const [profilePayload, eventsPayload, listingsPayload, deliveriesPayload, messagesPayload, financePayload] = await Promise.all([
        this.swapApi('/api/swap/seller/profile/'),
        this.swapApi('/api/swap/seller/events/'),
        this.swapApi('/api/swap/seller/listings/'),
        this.swapApi('/api/swap/seller/deliveries/'),
        this.swapApi('/api/swap/seller/messages/'),
        this.swapApi('/api/swap/seller/finance/'),
      ])
      if (profilePayload && profilePayload.err) {
        this.feedback = this.extractError(profilePayload, 'No pudimos cargar tu perfil Swap.')
        this.feedbackType = 'error'
      } else {
        this.profile = profilePayload ? profilePayload.profile : null
        this.market = profilePayload ? profilePayload.market : null
        this.syncProfileForm()
      }
      this.events = Array.isArray(eventsPayload) ? eventsPayload : []
      this.listings = Array.isArray(listingsPayload) ? listingsPayload : []
      this.deliveries = Array.isArray(deliveriesPayload) ? deliveriesPayload : []
      this.messageThreads = messagesPayload && Array.isArray(messagesPayload.conversations) ? messagesPayload.conversations : []
      this.finance = financePayload && !financePayload.err ? financePayload : null
      this.syncDeliveryHandoffForms()
      this.syncListingForms()
      if (!this.listingForm.event && this.events[0]) this.listingForm.event = this.events[0].id
      if (this.listingForm.event) await this.loadPricingOptions(this.listingForm.event)
      this.loading = false
    },
    async saveProfile() {
      this.savingProfile = true
      this.feedback = ''
      const data = await this.swapApi('/api/swap/seller/profile/', 'POST', this.profileForm)
      this.savingProfile = false
      if (data && data.id && !data.err) {
        this.profile = data
        this.syncProfileForm()
        this.feedback = 'Perfil guardado.'
        this.feedbackType = 'success'
        return
      }
      this.feedback = this.extractError(data, 'No pudimos guardar tu perfil.')
      this.feedbackType = 'error'
    },
    async createListing() {
      if (!this.canSubmitListing || this.savingListing) return
      this.savingListing = true
      this.feedback = ''
      const proofDraft = {
        file: this.listingForm.proof_file,
        external_reference: this.externalRefLines.join(' | '),
        note: this.listingForm.proof_note,
        delivery_method: this.listingForm.delivery_method,
      }
      const payload = {
        event: this.listingForm.event,
        official_ticket: this.listingForm.official_ticket,
        title: this.listingForm.title,
        section_label: this.listingForm.section_label,
        row_label: this.listingForm.row_label,
        quantity: Number(this.listingForm.quantity || 1),
        unit_price: this.listingForm.unit_price,
        currency: 'USD',
        delivery_method: this.listingForm.delivery_method,
        fulfillment_mode: this.listingForm.fulfillment_mode,
        seats_together: this.listingForm.seats_together,
        seat_labels: this.parseLines(this.listingForm.seat_labels_text),
        external_ticket_refs: this.listingExternalRefsForPayload(),
      }
      const data = await this.swapApi('/api/swap/seller/listings/', 'POST', payload)
      if (data && data.id && !data.err) {
        const proofError = await this.uploadListingDraftProof(data, proofDraft)
        this.savingListing = false
        this.feedback = proofError ? `Entrada publicada, pero no pudimos adjuntar la evidencia: ${proofError}` : 'Entrada publicada con evidencia lista.'
        this.feedbackType = proofError ? 'error' : 'success'
        const selectedEvent = this.listingForm.event
        this.listingForm = this.defaultListingForm()
        this.listingForm.event = selectedEvent
        this.saleStep = 'category'
        await this.loadAll({ preserveFeedback: true })
        return
      }
      this.savingListing = false
      this.feedback = this.extractError(data, 'No pudimos publicar la entrada.')
      this.feedbackType = 'error'
    },
    async uploadListingDraftProof(listing, draft) {
      if (!listing || !listing.id || !draft) return ''
      const hasFile = Boolean(draft.file)
      const externalReference = String(draft.external_reference || '').trim()
      if (!hasFile && !externalReference) return ''
      const payload = new FormData()
      if (hasFile) payload.append('file', draft.file)
      payload.append('artifact_type', hasFile ? 'ticket_file' : 'transfer_reference')
      payload.append('external_reference', externalReference)
      payload.append('note', draft.note || this.deliveryMethodLabel(draft.delivery_method))
      const data = await this.swapApiForm(`/api/swap/seller/listings/${listing.id}/proof/`, 'POST', payload, { timeoutMs: 60000 })
      if (data && data.id && !data.err) return ''
      return this.extractError(data, 'evidencia rechazada')
    },
    listingEditField(listing, field) {
      const form = this.listingEditForms[(listing && listing.id) || ''] || {}
      return Object.prototype.hasOwnProperty.call(form, field) ? form[field] : ''
    },
    setListingEditField(listing, field, value) {
      if (!listing || !listing.id) return
      this.listingEditForms = {
        ...this.listingEditForms,
        [listing.id]: {
          ...(this.listingEditForms[listing.id] || {}),
          [field]: value,
        },
      }
    },
    async saveListingEdit(listing) {
      if (!listing || !listing.id || this.savingListingEditKey) return
      const form = this.listingEditForms[listing.id] || {}
      const payload = {
        title: form.title || '',
        unit_price: form.unit_price || listing.unit_price,
        delivery_method: form.delivery_method || listing.delivery_method,
        fulfillment_mode: form.fulfillment_mode || listing.fulfillment_mode || 'per_unit',
      }
      if (String(form.seat_labels_text || '').trim()) payload.seat_labels = this.parseLines(form.seat_labels_text)
      if (String(form.external_refs_text || '').trim()) payload.external_ticket_refs = this.parseLines(form.external_refs_text)
      this.savingListingEditKey = `listing-${listing.id}`
      this.feedback = ''
      const data = await this.swapApi(`/api/swap/seller/listings/${listing.id}/`, 'PATCH', payload)
      this.savingListingEditKey = ''
      if (data && data.id && !data.err) {
        this.feedback = 'Entrada actualizada.'
        this.feedbackType = 'success'
        await this.loadAll({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos actualizar la entrada.')
      this.feedbackType = 'error'
    },
    listingProofField(listing, field) {
      const form = this.listingProofForms[(listing && listing.id) || ''] || {}
      return Object.prototype.hasOwnProperty.call(form, field) ? form[field] : ''
    },
    setListingProofField(listing, field, value) {
      if (!listing || !listing.id) return
      this.listingProofForms = {
        ...this.listingProofForms,
        [listing.id]: {
          ...(this.listingProofForms[listing.id] || {}),
          [field]: value,
        },
      }
    },
    setListingProofFile(listing, event) {
      const file = event && event.target && event.target.files && event.target.files[0] ? event.target.files[0] : null
      this.setListingProofField(listing, 'file', file)
    },
    canUploadProof(listing) {
      const form = this.listingProofForms[(listing && listing.id) || ''] || {}
      return Boolean(form.file || String(form.external_reference || '').trim())
    },
    async uploadListingProof(listing) {
      if (!listing || !listing.id || this.uploadingProofKey || !this.canUploadProof(listing)) return
      const form = this.listingProofForms[listing.id] || {}
      const payload = new FormData()
      if (form.file) payload.append('file', form.file)
      payload.append('artifact_type', form.file ? 'ticket_file' : 'transfer_reference')
      payload.append('external_reference', form.external_reference || '')
      payload.append('note', form.note || '')
      this.uploadingProofKey = `proof-${listing.id}`
      this.feedback = ''
      const data = await this.swapApiForm(`/api/swap/seller/listings/${listing.id}/proof/`, 'POST', payload, { timeoutMs: 60000 })
      this.uploadingProofKey = ''
      if (data && data.id && !data.err) {
        this.feedback = 'Evidencia guardada.'
        this.feedbackType = 'success'
        await this.loadAll({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos subir la evidencia.')
      this.feedbackType = 'error'
    },
    async runListingAction(listing, action) {
      if (!listing || !listing.id || this.listingActionKey) return
      if (action === 'cancel' && typeof window !== 'undefined' && !window.confirm('Cancelar esta entrada? No se puede deshacer.')) return
      this.listingActionKey = `${action}-${listing.id}`
      this.feedback = ''
      const data = await this.swapApi(`/api/swap/seller/listings/${listing.id}/action/`, 'POST', {
        action,
        note: `Seller action ${action} desde dashboard Swap.`,
      })
      this.listingActionKey = ''
      if (data && data.id && !data.err) {
        this.feedback = action === 'pause' ? 'Entrada pausada.' : action === 'resume' ? 'Entrada reactivada.' : 'Entrada cancelada.'
        this.feedbackType = 'success'
        await this.loadAll({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos cambiar esta entrada.')
      this.feedbackType = 'error'
    },
    async submitDeliveryHandoff(delivery) {
      if (!this.canSubmitDeliveryHandoffForm(delivery) || this.savingDeliveryKey) return
      this.savingDeliveryKey = `delivery-${delivery.id}`
      this.feedback = ''
      const form = this.deliveryHandoffForms[delivery.id] || {}
      const payload = new FormData()
      payload.append('evidence_reference', form.evidence_reference || '')
      payload.append('handoff_method', form.handoff_method || 'physical_handoff')
      payload.append('note', form.note || '')
      if (form.file) payload.append('evidence_file', form.file)
      const data = await this.swapApiForm(`/api/swap/seller/deliveries/${delivery.id}/handoff/`, 'POST', payload, { timeoutMs: 60000 })
      this.savingDeliveryKey = ''
      if (data && data.id && !data.err) {
        this.feedback = 'Entrega enviada a FOMO.'
        this.feedbackType = 'success'
        await this.loadAll({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos enviar la entrega.')
      this.feedbackType = 'error'
    },
    messageDraft(threadId) {
      return this.messageDrafts[threadId] || ''
    },
    setMessageDraft(threadId, value) {
      this.messageDrafts = { ...this.messageDrafts, [threadId]: value }
    },
    async sendSellerMessage(thread) {
      if (!thread || !thread.id || this.sendingMessageKey) return
      const body = String(this.messageDraft(thread.id) || '').trim()
      if (!body) return
      this.sendingMessageKey = `thread-${thread.id}`
      this.feedback = ''
      const data = await this.swapApi(`/api/swap/seller/messages/${thread.id}/`, 'POST', { body })
      this.sendingMessageKey = ''
      if (data && data.id && !data.err) {
        this.setMessageDraft(thread.id, '')
        this.feedback = 'Mensaje enviado a FOMO.'
        this.feedbackType = 'success'
        await this.loadAll({ preserveFeedback: true })
        return
      }
      this.feedback = this.extractError(data, 'No pudimos enviar tu mensaje.')
      this.feedbackType = 'error'
    },
  },
}
</script>

<style scoped>
.workspace-v2 {
  position: relative;
  min-height: 100vh;
  padding: 20px;
  background:
    radial-gradient(circle at top left, rgba(176, 185, 204, 0.08), transparent 24%),
    radial-gradient(circle at 82% 12%, rgba(76, 60, 94, 0.14), transparent 22%),
    linear-gradient(180deg, #06060b, #0a0910 42%, #0d0b14 100%);
}
.workspace-v2::before,
.workspace-v2::after { content: ''; position: fixed; pointer-events: none; z-index: 0; filter: blur(24px); }
.workspace-v2::before { top: 120px; right: 10%; width: 220px; height: 220px; border-radius: 999px; background: radial-gradient(circle, rgba(178, 188, 208, 0.14), rgba(178, 188, 208, 0)); }
.workspace-v2::after { bottom: 16%; left: 8%; width: 280px; height: 280px; border-radius: 999px; background: radial-gradient(circle, rgba(80, 62, 96, 0.14), rgba(80, 62, 96, 0)); }
.seller-swap-page { align-content: start; }
.seller-swap-page--embedded { min-height: 0; padding: 0; background: transparent; }
.seller-swap-page--embedded::before,
.seller-swap-page--embedded::after { display: none; }
.workspace-v2__shell { position: relative; z-index: 1; display: flex; align-items: flex-start; gap: 20px; max-width: 1440px; margin: 0 auto; }
.seller-swap-page--embedded .workspace-v2__shell { max-width: none; }
.workspace-v2__nav--mobile { display: none; }
.workspace-v2__nav--desktop { display: block; flex: 0 0 296px; width: 296px; align-self: flex-start; }
.workspace-v2__main { flex: 1 1 auto; min-width: 0; display: grid; gap: 18px; }
@media (min-width: 981px) {
  .workspace-v2__shell { overflow: visible; }
  .workspace-v2__shell > :deep(.organizer-nav) { position: static !important; inset: auto !important; top: auto !important; bottom: auto !important; left: auto !important; right: auto !important; align-self: start !important; height: auto !important; max-height: none !important; overflow: visible !important; transform: none !important; }
  .workspace-v2__shell > :deep(.organizer-nav__panel) { position: static !important; inset: auto !important; top: auto !important; bottom: auto !important; left: auto !important; right: auto !important; height: auto !important; max-height: none !important; overflow: visible !important; transform: none !important; }
}
.workspace-v2__kicker { color: rgba(201, 214, 230, 0.68); font-size: 0.68rem; font-weight: 800; letter-spacing: 0.16em; text-transform: uppercase; }
.seller-swap-embedded-menu { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; padding: 10px; border-radius: 28px; background: rgba(255, 255, 255, 0.035); border: 1px solid rgba(198, 208, 230, 0.1); }
.seller-swap-embedded-menu__item { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 2px 10px; align-items: center; min-height: 66px; padding: 12px 14px; border: 0; border-radius: 20px; color: rgba(232, 238, 248, 0.74); background: transparent; text-align: left; font-weight: 900; cursor: pointer; transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease; }
.seller-swap-embedded-menu__item i { grid-row: span 2; color: rgba(201, 214, 230, 0.72); }
.seller-swap-embedded-menu__item small { color: rgba(201, 214, 230, 0.56); font-size: 0.68rem; font-weight: 800; }
.seller-swap-embedded-menu__item.is-active,
.seller-swap-embedded-menu__item:hover { color: #fff; background: rgba(255, 255, 255, 0.08); transform: translateY(-1px); }
.seller-swap-hero, .seller-swap-panel, .seller-swap-profile-gate { border-radius: 28px; padding: 24px; }
.seller-swap-hero { display: grid; grid-template-columns: 1fr; gap: 14px; padding: 18px; background: radial-gradient(circle at 18% 0%, rgba(255, 255, 255, 0.08), transparent 34%), linear-gradient(180deg, rgba(22, 21, 32, 0.88), rgba(10, 10, 16, 0.94)); border: 1px solid rgba(198, 208, 230, 0.12); }
.seller-swap-hero h1, .seller-swap-panel h2, .seller-swap-panel h3, .seller-swap-profile-gate h2, .seller-swap-listing-card h3 { margin: 0; color: #f8fbff; }
.seller-swap-hero p, .seller-swap-panel p, .seller-swap-profile-gate p, .seller-swap-listing-card p { margin: 0; color: rgba(221, 231, 246, 0.76); }
.seller-swap-hero__bar { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.seller-swap-section-chip { display: grid; gap: 2px; min-width: 172px; padding: 10px 12px; border-radius: 16px; background: rgba(255,255,255,0.045); border: 1px solid rgba(198, 208, 230, 0.1); }
.seller-swap-section-chip small { color: rgba(214, 223, 240, 0.54); font-family: 'Space Grotesk', sans-serif; font-size: 0.62rem; font-weight: 800; letter-spacing: 0.18em; text-transform: uppercase; }
.seller-swap-section-chip strong { color: #f4f8ff; font-family: 'Syne', sans-serif; font-size: 0.98rem; letter-spacing: -0.04em; }
.seller-swap-hero__actions { display: inline-flex; align-items: center; justify-content: flex-end; gap: 10px; min-width: 0; }
.seller-swap-hero-action { display: inline-flex; align-items: center; justify-content: center; gap: 9px; min-height: 40px; padding: 0 14px; border: 1px solid rgba(255, 255, 255, 0.07); border-radius: 16px; color: #f6fbff; background: radial-gradient(circle at 18% 0%, rgba(255,255,255,0.1), transparent 34%), linear-gradient(180deg, rgba(26, 24, 36, 0.9), rgba(11, 10, 18, 0.96)); box-shadow: inset 0 1px 0 rgba(255,255,255,0.08); font-family: 'Space Grotesk', sans-serif; font-weight: 800; letter-spacing: -0.02em; cursor: pointer; }
.seller-swap-hero-action:disabled { opacity: 0.52; cursor: not-allowed; }
.seller-swap-hero__main { display: grid; grid-template-columns: minmax(0, 1fr); gap: 14px; align-items: stretch; }
.seller-swap-hero__copy { display: grid; align-content: center; gap: 7px; min-width: 0; }
.seller-swap-hero__copy h1 { max-width: 820px; font-family: 'Syne', sans-serif; font-size: clamp(1.55rem, 3vw, 2.35rem); line-height: 0.98; letter-spacing: -0.06em; }
.seller-swap-hero__copy p { max-width: 780px; font-size: 0.95rem; line-height: 1.45; }
.seller-swap-grid { display: grid; grid-template-columns: minmax(0, 0.82fr) minmax(0, 1.18fr); gap: 18px; }
.seller-swap-route-section { display: grid; gap: 18px; }
.seller-swap-profile-gate { display: flex; justify-content: space-between; gap: 18px; align-items: center; background: linear-gradient(135deg, rgba(37, 99, 235, 0.18), rgba(20, 184, 166, 0.12)); border: 1px solid rgba(120, 240, 202, 0.14); }
.seller-swap-panel { display: grid; gap: 18px; background: rgba(13, 20, 32, 0.82); border: 1px solid rgba(188, 207, 255, 0.12); }
.seller-swap-sell-experience { position: relative; display: grid; gap: 18px; overflow: hidden; padding: 22px; border-radius: 30px; background: radial-gradient(circle at 12% 0%, rgba(120, 240, 202, 0.18), transparent 28%), radial-gradient(circle at 88% 16%, rgba(109, 168, 255, 0.16), transparent 26%), linear-gradient(145deg, rgba(13, 20, 32, 0.94), rgba(9, 10, 18, 0.96)); border: 1px solid rgba(188, 207, 255, 0.14); }
.seller-swap-sell-experience::before { content: ''; position: absolute; inset: 16px 16px auto auto; width: 130px; height: 130px; border-radius: 999px; background: radial-gradient(circle, rgba(255, 255, 255, 0.1), transparent 64%); pointer-events: none; }
.seller-swap-sell-head { position: relative; display: flex; justify-content: space-between; gap: 18px; align-items: flex-start; }
.seller-swap-sell-head > div:first-child { display: grid; gap: 7px; max-width: 720px; }
.seller-swap-sell-head h2, .seller-swap-step-card h3 { margin: 0; color: #f8fbff; font-family: 'Syne', sans-serif; letter-spacing: -0.055em; }
.seller-swap-sell-head h2 { font-size: clamp(1.5rem, 2.4vw, 2.4rem); line-height: 0.98; }
.seller-swap-sell-head p, .seller-swap-step-card p { margin: 0; color: rgba(221, 231, 246, 0.72); line-height: 1.45; }
.seller-swap-ready-meter { display: grid; justify-items: center; min-width: 132px; padding: 14px; border-radius: 24px; color: rgba(221, 231, 246, 0.74); background: rgba(255,255,255,0.065); border: 1px solid rgba(188, 207, 255, 0.12); box-shadow: inset 0 1px 0 rgba(255,255,255,0.08); }
.seller-swap-ready-meter small, .seller-swap-ready-meter span { font-size: 0.72rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; }
.seller-swap-ready-meter strong { color: #95f5d0; font-size: 2rem; line-height: 1; }
.seller-swap-stepper { position: relative; display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 8px; }
.seller-swap-stepper__item { display: grid; gap: 7px; align-content: center; min-height: 74px; padding: 10px; border: 1px solid rgba(188, 207, 255, 0.12); border-radius: 18px; color: rgba(229, 237, 250, 0.68); background: rgba(255,255,255,0.045); cursor: pointer; text-align: left; }
.seller-swap-stepper__item span { display: inline-grid; place-items: center; width: 25px; height: 25px; border-radius: 999px; color: #071019; background: rgba(229, 237, 250, 0.54); font-size: 0.78rem; font-weight: 900; }
.seller-swap-stepper__item strong { font-size: 0.82rem; letter-spacing: -0.02em; }
.seller-swap-stepper__item.is-active { color: #f8fbff; border-color: rgba(120, 240, 202, 0.38); background: linear-gradient(145deg, rgba(120, 240, 202, 0.15), rgba(109, 168, 255, 0.1)); }
.seller-swap-stepper__item.is-active span, .seller-swap-stepper__item.is-done span { background: linear-gradient(135deg, #78f0ca, #6da8ff); }
.seller-swap-sale-lab { position: relative; display: grid; grid-template-columns: minmax(0, 1fr) minmax(300px, 360px); gap: 16px; align-items: start; }
.seller-swap-sale-stage { min-width: 0; }
.seller-swap-step-card { display: grid; gap: 16px; min-height: 430px; padding: 20px; border-radius: 26px; background: rgba(4, 8, 15, 0.44); border: 1px solid rgba(188, 207, 255, 0.12); box-shadow: inset 0 1px 0 rgba(255,255,255,0.06); }
.seller-swap-step-card h3 { font-size: clamp(1.42rem, 2.2vw, 2rem); }
.seller-swap-event-cards, .seller-swap-category-grid, .seller-swap-quantity-grid, .seller-swap-delivery-choice-grid { display: grid; gap: 12px; }
.seller-swap-event-cards { grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
.seller-swap-category-grid { grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); }
.seller-swap-quantity-grid { grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); }
.seller-swap-delivery-choice-grid { grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); }
.seller-swap-event-card, .seller-swap-category-card, .seller-swap-quantity-card, .seller-swap-delivery-choice { display: grid; gap: 8px; min-height: 122px; padding: 16px; border: 1px solid rgba(188, 207, 255, 0.13); border-radius: 22px; color: #f8fbff; background: linear-gradient(145deg, rgba(255,255,255,0.07), rgba(255,255,255,0.035)); cursor: pointer; text-align: left; transition: transform 160ms ease, border-color 160ms ease, background 160ms ease; }
.seller-swap-event-card:hover, .seller-swap-category-card:hover, .seller-swap-quantity-card:hover, .seller-swap-delivery-choice:hover { transform: translateY(-2px); border-color: rgba(120, 240, 202, 0.3); }
.seller-swap-event-card.is-active, .seller-swap-category-card.is-active, .seller-swap-quantity-card.is-active, .seller-swap-delivery-choice.is-active { border-color: rgba(120, 240, 202, 0.52); background: linear-gradient(145deg, rgba(120, 240, 202, 0.17), rgba(109, 168, 255, 0.12)); box-shadow: 0 18px 40px rgba(0,0,0,0.18); }
.seller-swap-event-card small, .seller-swap-category-card small { color: rgba(120, 240, 202, 0.78); font-size: 0.64rem; font-weight: 900; letter-spacing: 0.13em; text-transform: uppercase; }
.seller-swap-event-card strong, .seller-swap-category-card strong, .seller-swap-quantity-card strong, .seller-swap-delivery-choice strong { color: #f8fbff; font-size: 1.02rem; overflow-wrap: anywhere; }
.seller-swap-event-card span, .seller-swap-category-card span, .seller-swap-quantity-card span, .seller-swap-delivery-choice span { color: rgba(221, 231, 246, 0.7); font-size: 0.84rem; line-height: 1.35; }
.seller-swap-category-card em { color: #95f5d0; font-size: 0.82rem; font-style: normal; font-weight: 900; }
.seller-swap-category-card { grid-template-columns: auto minmax(0, 1fr); align-items: stretch; }
.seller-swap-category-card__icon { display: inline-grid; place-items: center; width: 46px; height: 46px; border-radius: 17px; color: #06101a !important; background: linear-gradient(135deg, #78f0ca, #6da8ff); font-size: 1.05rem !important; }
.seller-swap-category-card__body { display: grid; gap: 6px; min-width: 0; }
.seller-swap-category-card__price { grid-column: 1 / -1; display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 2px 10px; align-items: baseline; padding: 12px; border-radius: 18px; background: rgba(0, 0, 0, 0.16); border: 1px solid rgba(120, 240, 202, 0.12); }
.seller-swap-category-card__price small { color: rgba(221, 231, 246, 0.62) !important; }
.seller-swap-category-card__price strong { color: #95f5d0; font-size: 1.25rem; }
.seller-swap-category-card__price em { grid-column: 1 / -1; }
.seller-swap-quantity-card { min-height: 104px; place-items: center; text-align: center; }
.seller-swap-quantity-card strong { font-size: 2rem; line-height: 1; }
.seller-swap-price-step { align-content: start; }
.seller-swap-price-hero { display: grid; grid-template-columns: minmax(0, 1fr) minmax(220px, 0.42fr); gap: 14px; padding: 22px; border-radius: 28px; background: radial-gradient(circle at 20% 0%, rgba(120, 240, 202, 0.2), transparent 42%), linear-gradient(135deg, rgba(120, 240, 202, 0.12), rgba(109, 168, 255, 0.12)); border: 1px solid rgba(120, 240, 202, 0.22); }
.seller-swap-price-hero small { color: rgba(120, 240, 202, 0.84); font-size: 0.68rem; font-weight: 900; letter-spacing: 0.14em; text-transform: uppercase; }
.seller-swap-price-hero strong { color: #f8fbff; font-family: 'Syne', sans-serif; font-size: clamp(2.3rem, 5vw, 4.4rem); line-height: 0.9; letter-spacing: -0.08em; }
.seller-swap-price-hero span { color: rgba(221, 231, 246, 0.76); font-weight: 800; }
.seller-swap-price-hero > div { display: grid; gap: 7px; align-content: center; min-width: 0; }
.seller-swap-price-hero__source { padding: 14px; border-radius: 20px; background: rgba(3, 8, 14, 0.34); border: 1px solid rgba(120, 240, 202, 0.14); }
.seller-swap-price-hero__source i { color: #95f5d0; font-size: 1.25rem; }
.seller-swap-price-hero__source strong { font-family: 'Space Grotesk', sans-serif; font-size: 1rem; letter-spacing: -0.02em; line-height: 1.08; }
.seller-swap-price-bars { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.seller-swap-price-bars div, .seller-swap-final-summary, .seller-swap-price-context { display: grid; gap: 4px; padding: 13px; border-radius: 18px; background: rgba(255,255,255,0.055); border: 1px solid rgba(188, 207, 255, 0.1); }
.seller-swap-price-bars span, .seller-swap-final-summary span, .seller-swap-price-context span { color: rgba(221, 231, 246, 0.66); font-size: 0.74rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; }
.seller-swap-price-bars strong, .seller-swap-final-summary strong, .seller-swap-price-context strong { color: #f8fbff; }
.seller-swap-price-actions { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.seller-swap-price-input { display: grid; gap: 7px; min-width: 220px; color: rgba(221, 231, 246, 0.78); font-size: 0.84rem; font-weight: 800; }
.seller-swap-price-input input { width: 100%; border: 1px solid rgba(191, 206, 232, 0.16); border-radius: 16px; background: rgba(255,255,255,0.07); color: #f8fbff; padding: 12px 13px; outline: none; }
.seller-swap-price-guidance { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.seller-swap-price-guidance article { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 2px 10px; align-items: center; padding: 13px; border-radius: 18px; background: rgba(109, 168, 255, 0.08); border: 1px solid rgba(109, 168, 255, 0.14); }
.seller-swap-price-guidance i { grid-row: span 2; color: #95f5d0; }
.seller-swap-price-guidance strong { color: #f8fbff; }
.seller-swap-price-guidance span { color: rgba(221, 231, 246, 0.7); font-size: 0.82rem; }
.seller-swap-price-warning { padding: 12px 13px; border-radius: 16px; color: #ffe39a; background: rgba(251, 191, 36, 0.13); border: 1px solid rgba(251, 191, 36, 0.2); font-weight: 800; }
.seller-swap-sale-details { padding-top: 2px; }
.seller-swap-delivery-choice { grid-template-columns: auto minmax(0, 1fr); align-items: center; }
.seller-swap-delivery-choice > i { display: inline-grid; place-items: center; width: 44px; height: 44px; border-radius: 16px; color: #06101a; background: linear-gradient(135deg, #78f0ca, #6da8ff); }
.seller-swap-delivery-choice > span { display: grid; gap: 4px; }
.seller-swap-delivery-choice small { color: rgba(120, 240, 202, 0.82); font-size: 0.66rem; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; }
.seller-swap-delivery-choice em { color: rgba(221, 231, 246, 0.72); font-size: 0.84rem; font-style: normal; line-height: 1.35; }
.seller-swap-delivery-proof-card { display: grid; gap: 12px; padding: 16px; border-radius: 24px; background: linear-gradient(145deg, rgba(251, 191, 36, 0.1), rgba(109, 168, 255, 0.08)); border: 1px solid rgba(251, 191, 36, 0.22); }
.seller-swap-delivery-proof-card.is-ready { background: linear-gradient(145deg, rgba(120, 240, 202, 0.12), rgba(109, 168, 255, 0.09)); border-color: rgba(120, 240, 202, 0.25); }
.seller-swap-delivery-proof-card__head { display: grid; gap: 5px; }
.seller-swap-delivery-proof-card__head span { display: inline-flex; gap: 8px; align-items: center; color: rgba(120, 240, 202, 0.82); font-size: 0.7rem; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; }
.seller-swap-delivery-proof-card__head strong { color: #f8fbff; font-size: 1.2rem; }
.seller-swap-delivery-proof-card__head p { max-width: 760px; }
.seller-swap-file-drop { position: relative; display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 12px; align-items: center; padding: 16px; border-radius: 22px; color: #f8fbff; background: rgba(0, 0, 0, 0.18); border: 1px dashed rgba(120, 240, 202, 0.34); cursor: pointer; }
.seller-swap-file-drop input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
.seller-swap-file-drop__icon { display: inline-grid; place-items: center; width: 48px; height: 48px; border-radius: 18px; color: #06101a !important; background: linear-gradient(135deg, #78f0ca, #6da8ff); font-size: 1.1rem !important; }
.seller-swap-file-drop span:last-child { display: grid; gap: 4px; min-width: 0; }
.seller-swap-file-drop strong { overflow-wrap: anywhere; }
.seller-swap-file-drop small, .seller-swap-form label small { color: rgba(221, 231, 246, 0.66); font-size: 0.78rem; font-weight: 700; line-height: 1.35; }
.seller-swap-final-summary { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; padding: 0; background: transparent; border: 0; }
.seller-swap-final-summary article { display: grid; gap: 6px; padding: 14px; border-radius: 20px; background: rgba(255,255,255,0.055); border: 1px solid rgba(188, 207, 255, 0.1); }
.seller-swap-final-summary i { color: #95f5d0; font-size: 1rem; }
.seller-swap-final-summary em, .seller-swap-price-context em { color: rgba(221, 231, 246, 0.72); font-style: normal; }
.seller-swap-price-context { grid-template-columns: repeat(3, minmax(0, 1fr)) auto; align-items: center; }
.seller-swap-price-context .seller-swap-button { min-height: 36px; padding: 8px 12px; white-space: nowrap; }
.seller-swap-publish-button { min-height: 48px; font-size: 1rem; }
.seller-swap-live-preview { position: sticky; top: 18px; display: grid; gap: 12px; padding: 16px; border-radius: 28px; background: linear-gradient(180deg, rgba(8, 12, 20, 0.9), rgba(4, 7, 13, 0.94)); border: 1px solid rgba(188, 207, 255, 0.12); box-shadow: 0 24px 60px rgba(0,0,0,0.22); }
.seller-swap-live-preview__visual, .seller-swap-live-preview__price { display: grid; gap: 7px; padding: 16px; border-radius: 22px; background: rgba(255,255,255,0.055); border: 1px solid rgba(188, 207, 255, 0.1); }
.seller-swap-live-preview__visual { min-height: 210px; align-content: flex-end; background: radial-gradient(circle at 18% 0%, rgba(120, 240, 202, 0.24), transparent 42%), radial-gradient(circle at 82% 8%, rgba(109, 168, 255, 0.2), transparent 36%), linear-gradient(145deg, rgba(20, 27, 44, 0.92), rgba(9, 10, 18, 0.96)); }
.seller-swap-live-preview__icon { display: inline-grid; place-items: center; width: 52px; height: 52px; border-radius: 18px; color: #06101a !important; background: linear-gradient(135deg, #78f0ca, #6da8ff); font-size: 1.2rem !important; }
.seller-swap-live-preview small { color: rgba(120, 240, 202, 0.76); font-size: 0.65rem; font-weight: 900; letter-spacing: 0.13em; text-transform: uppercase; }
.seller-swap-live-preview strong { color: #f8fbff; font-family: 'Syne', sans-serif; font-size: 1.35rem; line-height: 1; letter-spacing: -0.05em; overflow-wrap: anywhere; }
.seller-swap-live-preview__price strong { font-size: 2rem; color: #95f5d0; }
.seller-swap-live-preview span { color: rgba(221, 231, 246, 0.72); line-height: 1.38; }
.seller-swap-live-preview__proof { display: flex; gap: 9px; align-items: center; padding: 12px; border-radius: 18px; color: #ffe39a; background: rgba(251, 191, 36, 0.12); border: 1px solid rgba(251, 191, 36, 0.18); font-weight: 900; }
.seller-swap-live-preview__proof.is-ready { color: #b9ffe4; background: rgba(120, 240, 202, 0.1); border-color: rgba(120, 240, 202, 0.18); }
.seller-swap-live-preview__proof.is-neutral { color: #cfe2ff; background: rgba(109, 168, 255, 0.1); border-color: rgba(109, 168, 255, 0.18); }
.seller-swap-live-preview__badges { display: flex; flex-wrap: wrap; gap: 8px; }
.seller-swap-live-preview__badges span { display: inline-flex; border-radius: 999px; padding: 7px 10px; color: #dffcf0; background: rgba(120, 240, 202, 0.12); border: 1px solid rgba(120, 240, 202, 0.18); font-size: 0.75rem; font-weight: 900; }
.seller-swap-panel__header { display: flex; justify-content: space-between; gap: 14px; align-items: flex-start; }
.seller-swap-panel__header--stacked { align-items: center; }
.seller-swap-panel__header--compact { align-items: center; }
.seller-swap-form { display: grid; gap: 12px; }
.seller-swap-form--two { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.seller-swap-form label { display: grid; gap: 7px; color: rgba(221, 231, 246, 0.78); font-size: 0.84rem; font-weight: 700; }
.seller-swap-field--wide { grid-column: 1 / -1; }
.seller-swap-form input, .seller-swap-form select, .seller-swap-form textarea { width: 100%; border: 1px solid rgba(191, 206, 232, 0.16); border-radius: 16px; background: rgba(255,255,255,0.07); color: #f8fbff; padding: 12px 13px; outline: none; }
.seller-swap-form textarea { resize: vertical; }
.seller-swap-check { display: flex !important; grid-template-columns: auto 1fr; align-items: center; }
.seller-swap-check input { width: auto; }
.seller-swap-check--inline { align-self: flex-end; padding-bottom: 11px; }
.seller-swap-button { display: inline-flex; align-items: center; justify-content: center; border: 1px solid rgba(190, 211, 255, 0.18); border-radius: 999px; padding: 11px 16px; color: #e8f0ff; background: rgba(255,255,255,0.06); font-weight: 800; text-decoration: none; cursor: pointer; }
.seller-swap-button--primary { background: linear-gradient(135deg, #78f0ca, #6da8ff); border: none; color: #06101a; }
.seller-swap-button--danger { border-color: rgba(248, 113, 113, 0.32); color: #ffd2d2; background: rgba(248, 113, 113, 0.13); }
.seller-swap-button:disabled { opacity: 0.5; cursor: not-allowed; }
.seller-swap-link-button { white-space: nowrap; }
.seller-swap-pill { display: inline-flex; align-items: center; justify-content: center; border-radius: 999px; padding: 7px 10px; font-size: 0.78rem; font-weight: 900; text-transform: uppercase; background: rgba(255,255,255,0.08); color: rgba(236, 243, 255, 0.8); }
.seller-swap-pill.is-active { background: rgba(52, 211, 153, 0.16); color: #95f5d0; }
.seller-swap-pill.is-warning { background: rgba(251, 191, 36, 0.16); color: #ffe39a; }
.seller-swap-pill.is-danger { background: rgba(248, 113, 113, 0.17); color: #ffc0c0; }
.seller-swap-pill.is-muted { background: rgba(148, 163, 184, 0.16); color: #d7e1ef; }
.seller-swap-feedback { padding: 13px 16px; border-radius: 18px; font-weight: 800; }
.seller-swap-feedback.is-success { background: rgba(52, 211, 153, 0.14); color: #b9ffe4; border: 1px solid rgba(52, 211, 153, 0.22); }
.seller-swap-feedback.is-error { background: rgba(248, 113, 113, 0.16); color: #ffd2d2; border: 1px solid rgba(248, 113, 113, 0.24); }
.seller-swap-empty { padding: 22px; border-radius: 20px; background: rgba(255,255,255,0.04); color: rgba(221, 231, 246, 0.76); }
.seller-swap-entry-filters { display: flex; flex-wrap: wrap; gap: 8px; }
.seller-swap-entry-filter { display: inline-flex; align-items: center; gap: 8px; min-height: 38px; padding: 0 12px; border: 1px solid rgba(188, 207, 255, 0.12); border-radius: 999px; color: rgba(229, 237, 250, 0.72); background: rgba(255,255,255,0.045); font-weight: 900; cursor: pointer; }
.seller-swap-entry-filter strong { display: inline-grid; place-items: center; min-width: 24px; height: 24px; border-radius: 999px; color: #071019; background: rgba(120, 240, 202, 0.86); font-size: 0.76rem; }
.seller-swap-entry-filter.is-active { color: #f8fbff; border-color: rgba(120, 240, 202, 0.35); background: rgba(120, 240, 202, 0.1); }
.seller-swap-entry-table { display: grid; gap: 10px; }
.seller-swap-entry-row { display: grid; gap: 0; border-radius: 22px; overflow: hidden; background: rgba(255,255,255,0.052); border: 1px solid rgba(188, 207, 255, 0.12); }
.seller-swap-entry-row__main { display: grid; grid-template-columns: minmax(0, 1.45fr) minmax(210px, 0.8fr) minmax(150px, 0.45fr) minmax(220px, 0.6fr); gap: 14px; align-items: center; padding: 14px; }
.seller-swap-entry-row__entry, .seller-swap-entry-row__money { display: grid; gap: 4px; min-width: 0; }
.seller-swap-entry-row__entry small, .seller-swap-entry-row__money small { color: rgba(120, 240, 202, 0.78); font-size: 0.66rem; font-weight: 900; letter-spacing: 0.13em; text-transform: uppercase; }
.seller-swap-entry-row__entry strong, .seller-swap-entry-row__money strong { color: #f8fbff; overflow-wrap: anywhere; }
.seller-swap-entry-row__entry span, .seller-swap-entry-row__entry em, .seller-swap-entry-row__money span { color: rgba(221, 231, 246, 0.7); font-size: 0.84rem; font-style: normal; overflow-wrap: anywhere; }
.seller-swap-entry-row__status, .seller-swap-entry-row__actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.seller-swap-entry-row__actions { justify-content: flex-end; }
.seller-swap-entry-row__actions .seller-swap-button { min-height: 38px; padding: 8px 12px; }
.seller-swap-entry-row__details { display: grid; gap: 12px; padding: 14px; border-top: 1px solid rgba(188, 207, 255, 0.1); background: rgba(0, 0, 0, 0.12); }
.seller-swap-listings { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 14px; }
.seller-swap-delivery-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 14px; }
.seller-swap-message-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(310px, 1fr)); gap: 14px; }
.seller-swap-finance-grid { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 12px; }
.seller-swap-finance-card { display: grid; gap: 8px; min-height: 132px; padding: 16px; border-radius: 22px; background: linear-gradient(145deg, rgba(120, 240, 202, 0.1), rgba(109, 168, 255, 0.08)); border: 1px solid rgba(188, 207, 255, 0.12); }
.seller-swap-finance-card small { color: rgba(120, 240, 202, 0.78); font-size: 0.66rem; font-weight: 900; letter-spacing: 0.13em; text-transform: uppercase; }
.seller-swap-finance-card strong { color: #f8fbff; font-size: 1.35rem; }
.seller-swap-finance-card span { color: rgba(221, 231, 246, 0.68); font-size: 0.82rem; }
.seller-swap-finance-split { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(320px, 0.8fr); gap: 14px; }
.seller-swap-finance-list { display: grid; gap: 12px; padding: 16px; border-radius: 24px; background: rgba(255,255,255,0.045); border: 1px solid rgba(188, 207, 255, 0.1); }
.seller-swap-row-list { display: grid; gap: 10px; }
.seller-swap-row-card { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; padding: 13px; border-radius: 18px; background: rgba(255,255,255,0.055); border: 1px solid rgba(188, 207, 255, 0.1); }
.seller-swap-row-card div { display: grid; gap: 4px; min-width: 0; }
.seller-swap-row-card div:last-child { justify-items: flex-end; }
.seller-swap-row-card strong { color: #f8fbff; }
.seller-swap-row-card span, .seller-swap-row-card small { color: rgba(221, 231, 246, 0.7); overflow-wrap: anywhere; }
.seller-swap-listing-card, .seller-swap-delivery-card { display: grid; gap: 10px; padding: 16px; border-radius: 22px; background: rgba(255,255,255,0.06); border: 1px solid rgba(188, 207, 255, 0.12); }
.seller-swap-delivery-card { background: linear-gradient(145deg, rgba(22, 38, 61, 0.86), rgba(12, 32, 38, 0.82)); }
.seller-swap-artifact-list { display: grid; gap: 8px; }
.seller-swap-artifact-list article { display: grid; gap: 4px; padding: 11px; border-radius: 16px; background: rgba(255,255,255,0.055); border: 1px solid rgba(120, 240, 202, 0.14); }
.seller-swap-artifact-list small { color: rgba(120, 240, 202, 0.76); font-size: 0.64rem; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase; }
.seller-swap-artifact-list strong { color: #f8fbff; }
.seller-swap-artifact-list span { color: rgba(221, 231, 246, 0.72); font-size: 0.84rem; overflow-wrap: anywhere; }
.seller-swap-artifact-list a { color: #95f5d0; font-weight: 900; overflow-wrap: anywhere; }
.seller-swap-message-card { display: grid; gap: 12px; padding: 16px; border-radius: 22px; background: linear-gradient(145deg, rgba(18, 30, 54, 0.86), rgba(11, 30, 37, 0.82)); border: 1px solid rgba(188, 207, 255, 0.12); }
.seller-swap-message-card h3 { margin: 0; color: #f8fbff; }
.seller-swap-message-card p { margin: 0; color: rgba(221, 231, 246, 0.76); }
.seller-swap-listing-card__top { display: flex; justify-content: space-between; gap: 10px; color: rgba(221, 231, 246, 0.78); }
.seller-swap-listing-card__meta { display: grid; gap: 6px; color: rgba(221, 231, 246, 0.74); font-size: 0.9rem; }
.seller-swap-listing-card__meta span { display: inline-flex; gap: 8px; align-items: center; }
.seller-swap-proof-strip { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; border-radius: 18px; padding: 12px; background: rgba(120, 240, 202, 0.08); border: 1px solid rgba(120, 240, 202, 0.16); }
.seller-swap-proof-strip div { display: grid; gap: 3px; min-width: 0; }
.seller-swap-proof-strip small { color: rgba(120, 240, 202, 0.78); font-size: 0.66rem; font-weight: 900; letter-spacing: 0.13em; text-transform: uppercase; }
.seller-swap-proof-strip strong { color: #f8fbff; }
.seller-swap-proof-strip span { color: rgba(221, 231, 246, 0.7); font-size: 0.84rem; overflow-wrap: anywhere; }
.seller-swap-proof-strip a { color: #95f5d0; font-weight: 900; white-space: nowrap; }
.seller-swap-listing-edit, .seller-swap-proof-form { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; padding-top: 4px; }
.seller-swap-listing-edit label, .seller-swap-proof-form label { display: grid; gap: 7px; color: rgba(221, 231, 246, 0.78); font-size: 0.84rem; font-weight: 700; }
.seller-swap-listing-edit input, .seller-swap-listing-edit select, .seller-swap-listing-edit textarea, .seller-swap-proof-form input { width: 100%; border: 1px solid rgba(191, 206, 232, 0.16); border-radius: 16px; background: rgba(255,255,255,0.07); color: #f8fbff; padding: 12px 13px; outline: none; }
.seller-swap-listing-edit textarea { resize: vertical; }
.seller-swap-actions-row { display: flex; flex-wrap: wrap; gap: 8px; }
.seller-swap-delivery-form { display: grid; gap: 10px; padding-top: 4px; }
.seller-swap-delivery-form label { display: grid; gap: 7px; color: rgba(221, 231, 246, 0.78); font-size: 0.84rem; font-weight: 700; }
.seller-swap-delivery-form input { width: 100%; border: 1px solid rgba(191, 206, 232, 0.16); border-radius: 16px; background: rgba(255,255,255,0.07); color: #f8fbff; padding: 12px 13px; outline: none; }
.seller-swap-delivery-lock { border-radius: 16px; padding: 12px 13px; background: rgba(255,255,255,0.05); color: rgba(221, 231, 246, 0.72); font-weight: 700; }
.seller-swap-thread-messages { display: grid; gap: 8px; max-height: 260px; overflow: auto; padding-right: 4px; }
.seller-swap-thread-message { display: grid; gap: 4px; padding: 10px 11px; border-radius: 15px; background: rgba(255,255,255,0.055); border: 1px solid rgba(188, 207, 255, 0.1); }
.seller-swap-thread-message small { color: rgba(120, 240, 202, 0.78); font-size: 0.66rem; font-weight: 900; letter-spacing: 0.13em; text-transform: uppercase; }
.seller-swap-thread-message span { color: rgba(231, 240, 252, 0.84); line-height: 1.45; }
.seller-swap-thread-message.is-system { border-color: rgba(120, 240, 202, 0.18); }
.seller-swap-thread-message.is-admin { background: rgba(120, 240, 202, 0.1); }
.seller-swap-thread-message.is-seller { background: rgba(109, 168, 255, 0.12); }
.seller-swap-message-compose { display: grid; gap: 10px; }
.seller-swap-message-compose label { display: grid; gap: 7px; color: rgba(221, 231, 246, 0.78); font-size: 0.84rem; font-weight: 700; }
.seller-swap-message-compose--inline { padding: 12px; border-radius: 18px; background: rgba(109, 168, 255, 0.08); border: 1px solid rgba(109, 168, 255, 0.14); }
.seller-swap-message-compose textarea { width: 100%; border: 1px solid rgba(191, 206, 232, 0.16); border-radius: 16px; background: rgba(255,255,255,0.07); color: #f8fbff; padding: 12px 13px; outline: none; resize: vertical; }
@media (max-width: 1180px) {
  .seller-swap-stepper { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .seller-swap-sale-lab { grid-template-columns: 1fr; }
  .seller-swap-live-preview { position: static; grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .seller-swap-live-preview__badges { grid-column: 1 / -1; }
  .seller-swap-entry-row__main { grid-template-columns: minmax(0, 1fr) minmax(190px, 0.7fr); }
  .seller-swap-entry-row__actions { justify-content: flex-start; }
  .seller-swap-finance-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .seller-swap-finance-split { grid-template-columns: 1fr; }
}
@media (max-width: 980px) {
  .workspace-v2 { padding: 12px; }
  .workspace-v2__nav--mobile { display: block; }
  .workspace-v2__nav--desktop { display: none; }
  .workspace-v2__shell { display: grid; grid-template-columns: 1fr; }
  .seller-swap-hero { padding: 14px; border-radius: 24px; }
  .seller-swap-embedded-menu { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .seller-swap-hero__main, .seller-swap-grid, .seller-swap-form--two, .seller-swap-listing-edit, .seller-swap-proof-form, .seller-swap-live-preview { grid-template-columns: 1fr; }
  .seller-swap-hero__bar { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 10px; }
  .seller-swap-section-chip { min-width: 0; }
  .seller-swap-hero__actions { justify-content: flex-end; width: auto; }
  .seller-swap-hero-action { min-height: 43px; padding: 0 12px; }
  .seller-swap-hero__copy h1 { font-size: 1.42rem; line-height: 1.04; }
  .seller-swap-hero__copy p { display: -webkit-box; overflow: hidden; font-size: 0.84rem; line-height: 1.35; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
  .seller-swap-sell-experience { padding: 14px; border-radius: 24px; }
  .seller-swap-sell-head { display: grid; }
  .seller-swap-ready-meter { justify-items: start; min-width: 0; }
  .seller-swap-step-card { min-height: auto; padding: 16px; border-radius: 22px; }
  .seller-swap-price-hero, .seller-swap-price-bars, .seller-swap-price-context, .seller-swap-price-guidance, .seller-swap-final-summary { grid-template-columns: 1fr; }
  .seller-swap-price-actions { display: grid; }
  .seller-swap-price-input { min-width: 0; }
  .seller-swap-profile-gate { display: grid; }
  .seller-swap-panel__header, .seller-swap-panel__header--stacked { display: grid; }
  .seller-swap-entry-filters { flex-wrap: nowrap; overflow-x: auto; padding-bottom: 2px; }
  .seller-swap-entry-filter { flex: 0 0 auto; }
  .seller-swap-entry-row__main { grid-template-columns: 1fr; gap: 10px; }
  .seller-swap-entry-row__actions { justify-content: flex-start; }
  .seller-swap-proof-strip { flex-direction: column; }
  .seller-swap-finance-grid { grid-template-columns: 1fr; }
  .seller-swap-row-card { display: grid; }
  .seller-swap-row-card div:last-child { justify-items: start; }
}
@media (max-width: 640px) {
  .seller-swap-hero__bar { grid-template-columns: 1fr; }
  .seller-swap-hero__actions { justify-content: stretch; }
  .seller-swap-hero-action { width: 100%; }
  .seller-swap-embedded-menu { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .seller-swap-embedded-menu__item { min-height: 58px; padding: 10px 12px; }
  .seller-swap-stepper { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); overflow: visible; padding-bottom: 0; }
  .seller-swap-stepper__item { min-height: 64px; padding: 10px; }
  .seller-swap-event-cards, .seller-swap-category-grid, .seller-swap-quantity-grid, .seller-swap-delivery-choice-grid { grid-template-columns: 1fr; }
  .seller-swap-live-preview__visual { min-height: 170px; }
  .seller-swap-entry-row__actions .seller-swap-button, .seller-swap-button { width: 100%; }
}
</style>
